import React, { useEffect, useState, useContext, useRef } from "react";
import {
    ThemeContext,
    PatientContext,
    //InputVisibleContext,
} from "../lib/main-context";
import { useTranslation } from "../i18n/TranslationProvider";
import { translateOr } from "../i18n/translateData";

import { useHistory, useLocation } from "react-router-dom";
import "react-day-picker/lib/style.css";
import { Row, Col, Alert, Collapse, Card, Button, Modal } from "react-bootstrap";
import { videoGroups } from "./VideoConfig";
import "./VideoChecklist.css";

//components
import Sallie from "./Sallie";
import FeelingToday from "./DashboardComponents/FeelingToday";
import Calendar from "./DashboardComponents/Calendar";
import EditNotification from "./DashboardComponents/EditNotification";
import SymptomPicker from "./DashboardComponents/SymptomPicker";
//import SymptomTracker from "./DashboardComponents/SymptomTracker";
import ComponentWrapper from "./DashboardComponents/ComponentWrapper";
import AdditionalNote from "./DashboardComponents/AdditionalNote";
import TriggerSelector from "./DashboardComponents/TriggerSelector";
import ThemeChanger from "./ThemeChanger";
import AddSymptoms from "./DashboardComponents/AddSymptoms";
import Charts from "./DashboardComponents/Charts";
import LoadingSpinner from "./LoadingSpinner";
import CurrentPatient from "./DashboardComponents/CurrentPatient";
import OnOffButtonMessages from "./DashboardComponents/OnOffButtonMessages";
import OnOffButtonAlert from "./DashboardComponents/OnOffButtonAlert";
import PatientStatus from "./DashboardComponents/PatientStatus";
import AssignmentButton from "./DashboardComponents/AssignmentButton";
import HemisphericSum from "./DashboardComponents/HemisphericSum/HemisphericSumContainer";
//import TherapyTracker from "./DashboardComponents/TherapyTracker/TherapyTrackerContainer";
//import VideoTracker from "./DashboardComponents/VideoTracker";

//lib
import customizeMessages from "../lib/customizeMessages";
import useWindowSize from "../lib/useWindowSize";
import getLoggedDataToday from "../lib/getLoggedDataToday";

//api
import {
    getAdditionalNote,
    getPartiallyTrackedDays,
    getTrackedDays,
} from "../api/TBIRequests";
import { getPatientRegistrationProgress } from "../api/TBIRequests";
import { sendAssignments, getPatientExercises, scheduleAppointmentEmail, recordPatientExercise, removePatientExercises } from "../api/adminRequests";
import emailAlert from "../images/email-notification.svg";
import ExerciseChangeAlert from "./DashboardComponents/ExerciseChangeAlert";
import theme from "../index.scss";

const Dashboard = ({
    user,
    loadTrackedData,
    loadingTrackedData,
    trackedData,
    errorLoadingTrackedData,
    topSymptoms,
    setTopSymptoms,
    loadingTopSymptoms,
    loadingFeelingToday,
    doneSimpleTracking,
    setDoneSimpleTracking,
    loadingTopTrigger,
    topTrigger,
    errorLoadingTopTrigger,
    loadTrigger,
    messages,
    setCaregiverList,
    setPatientList,
    patientList,
    caregiverList,
    onOffButtonMessagesStatus,
    setOnOffButtonMessagesStatus,
    //inputVisible,
    //setInputVisible,
}) => {
    const { patient } = useContext(PatientContext);
    const { t, language } = useTranslation();

    // Exercise reference data (names/categories/groups come from the DB / video config).
    // Each returns null when no translation key exists, so callers can fall back.
    const tExerciseName = (name) => translateOr(t, `dashboard.exerciseNames.${name}`, null);
    const tExerciseCategory = (slug) => translateOr(t, `dashboard.exerciseCategories.${slug}`, null);
    const tExerciseGroup = (id) => translateOr(t, `dashboard.exerciseGroups.${id}`, null);
    const levelWord = t("dashboard.ui.level");
    const { dark } = useContext(ThemeContext);

    const [width] = useWindowSize();
    const history = useHistory();
    const location = useLocation();

    // Filter out Intro group from videoGroups for this Dashboard component only
    const filteredVideoGroups = videoGroups
        .filter(group => 
            group.id !== 'intro' && 
            group.name !== 'Intro' && 
            !group.name.toLowerCase().includes('intro') &&
            group.id !== 'supplements' &&  
            group.id !== 'sallie-instructions'  
        )
        .map(group => {
            if (group.id === 'primitive-reflex') {
                return {
                    ...group,
                    // Filter out 'Melillo-Intro to the Home Program' from main items
                    items: (group.items || []).filter(item => item.id !== 'pr-intro-home'),
                    // Filter subgroups to remove specific intro videos
                    subgroups: (group.subgroups || []).map(subgroup => ({
                        ...subgroup,
                        items: subgroup.items.filter(item => 
                            item.id !== 'pr-dr-intro' &&  
                            item.id !== 'pr-intro-1'       
                        )
                    }))
                };
            }
            
            // For the sensory-equipment group, filter out instructional videos from specific subgroups
            if (group.id === 'sensory-equipment') {
                return {
                    ...group,
                    items: (group.items || []).filter(item => 
                        item.id !== 'galvanic' &&
                        item.id !== 'bone-conduction' &&
                        item.id !== 'tens'
                    ),
                    subgroups: (group.subgroups || []).map(subgroup => {
                        if (subgroup.id === 'transcranial') {
                            return {
                                ...subgroup,
                                items: subgroup.items.filter(item => 
                                    item.id !== 'trans-brain-driver' &&
                                    item.id !== 'trans-saline' &&
                                    item.id !== 'trans-level-select'
                                )
                            };
                        }
                        else if (subgroup.id === 'pons') {
                            return {
                                ...subgroup,
                                items: subgroup.items.filter(item => 
                                    item.id !== 'pons-intro' &&
                                    item.id !== 'pons-practical'
                                )
                            };
                        }
                        else if (subgroup.id === 'photobiomodulation') {
                            return {
                                ...subgroup,
                                items: (subgroup.items || []).filter(item =>
                                    item.id !== 'laser-intro' &&
                                    item.id !== 'laser-setup'
                                ),
                                subgroups: subgroup.subgroups
                            };
                        }
                        return subgroup;
                    }).filter(subgroup => 
                        !(subgroup.id === 'eyelights' || subgroup.name.toLowerCase().includes('eyelight'))
                    )
                };
            }
            
            return group;
    });

    const [date, setDate] = useState(new Date());

    const [currentTrackingSymptom, setCurrentTrackingSymptom] = useState(0);
    const [currentTrackingStep, setCurrentTrackingStep] = useState(0);
    const [additionalNotes, setAdditionalNotes] = useState([]);
    const [showPopUp, setShowPopUp] = useState(true);
    const [status, setStatus] = useState("Active");
    const [firstUnfinishedStep, setFirstUnfinishedStep] = useState("");

    const [trackedDays, setTrackedDays] = useState([]);
    const [partiallyTrackedDays, setPartiallyTrackedDays] = useState([]);
    const [untrackedDays, setUntrackedDays] = useState([]);

    const [skippedUnloggedSymptoms, setSkippedUnloggedSymptoms] = useState(0);

    // Video Exercise Tracker states
    //const [showVideoTracker, setShowVideoTracker] = useState(false);
    //const [expandedVideoSections, setExpandedVideoSections] = useState({});
    //const [exerciseCounts, setExerciseCounts] = useState({});
    //const [checkedVideos, setCheckedVideos] = useState({});
    const [videoCounts, setVideoCounts] = useState({});
    //const scrollContainerRef = React.useRef(null);

    // Video Assignment states (for Administrative Support providers)
    const [showAssignmentDropdown, setShowAssignmentDropdown] = useState(false);
    const [expandedAssignmentSections, setExpandedAssignmentSections] = useState({});
    const [selectedVideos, setSelectedVideos] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    
    // New states for refactored assignment UI
    const [activeGroupInput, setActiveGroupInput] = useState(null); // { groupId, categoryId }
    const [levelInputValue, setLevelInputValue] = useState('');
    const [selectedExercisesForAssignment, setSelectedExercisesForAssignment] = useState([]); // Array of { groupId, categoryId, level, displayName }

    // Assigned exercises state (for patients and caregivers)
    const [assignedExercises, setAssignedExercises] = useState({});
    const [loadingExercises, setLoadingExercises] = useState(true);
    const [appointmentEmailSent, setAppointmentEmailSent] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    
    // Provider view of patient's assigned exercises
    const [providerViewAssignedExercises, setProviderViewAssignedExercises] = useState({});
    const [loadingProviderViewExercises, setLoadingProviderViewExercises] = useState(false);
    const [exerciseChangeAlertDismissed, setExerciseChangeAlertDismissed] = useState(false);
    //const [expandedCategories, setExpandedCategories] = useState({}); // Track which category ovals are expanded
    
    // Update Exercise modal state
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [exercisesToUpdate, setExercisesToUpdate] = useState([]);
    const [originalExercises, setOriginalExercises] = useState([]); // Track original exercises to detect deletions
    const [updatingExercises, setUpdatingExercises] = useState(false);
    
    // Rate limiting state for exercise increments (3 per 24 hours)
    const [exerciseIncrementLogs, setExerciseIncrementLogs] = useState([]);
    const [showRateLimitModal, setShowRateLimitModal] = useState(false);
    const MAX_INCREMENTS_PER_DAY = 2;
    
    // Custom Alert Modal state (to replace native alert with translatable modal)
    const [showCustomAlert, setShowCustomAlert] = useState(false);
    const [customAlertMessage, setCustomAlertMessage] = useState('');

    // Level-Up Modal state — shown after the 5th consecutive "+" completes a level's progress bar
    const [showLevelUpModal, setShowLevelUpModal] = useState(false);
    const [levelUpExercise, setLevelUpExercise] = useState(null);
    const [savingLevelUp, setSavingLevelUp] = useState(false);
    const [levelUpSaveError, setLevelUpSaveError] = useState(false);

    // Decrement Confirm Modal state — shown when the patient clicks "-", before their
    // progress is actually reset. Nothing changes until "Save" is clicked.
    const [showDecrementConfirmModal, setShowDecrementConfirmModal] = useState(false);
    const [decrementExercise, setDecrementExercise] = useState(null);

    // Queue of exercises found sitting at 5/5 that still need their popup/alert surfaced.
    // Covers exercises that were already stuck at 5/5 (e.g. from before this flow existed,
    // or a previously-failed "Save Progress") as well as multiple exercises completing
    // close together — see enqueuePendingCompletions below.
    const [completionQueue, setCompletionQueue] = useState([]);
    // Cap/max alerts are dismissible without resolving anything, so track which exercise
    // ids we've already alerted on this session to avoid re-nagging on every data refresh.
    const [seenCapOrMaxIds, setSeenCapOrMaxIds] = useState(new Set());
    
    // Track which exercises have hit their daily limit (exercise_id -> boolean)
    const [exerciseDailyLimitReached, setExerciseDailyLimitReached] = useState({});

    // Automatically clear the daily limit alert at midnight for users who stay on the dashboard
    useEffect(() => {
        const now = new Date();
        const msUntilMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1) - now;
        const timer = setTimeout(() => {
            setExerciseDailyLimitReached({});
        }, msUntilMidnight);
        return () => clearTimeout(timer);
    }, []);

    const checkUnreadMessagesPresent = (messages) => {
        const isProvider = user.userType === "provider";
        const isPatient = user.userType !== "provider";

        return (
            messages &&
            messages.some((message) => {
                const belongsToProvider = message.providerId === user.userId;
                const isFromDirection = message.direction === "from";
                const isToDirection = message.direction === "to";
                const hasBeenRead = message.hasBeenRead;

                // Logic for showing Mark as Read button
                const showMarkAsReadButton =
                    // Patient: Message is from a provider, and unread
                    (!hasBeenRead && isPatient && isFromDirection) ||
                    // Provider: Message is to the provider, unread, and has matching providerId
                    (!hasBeenRead &&
                        isProvider &&
                        isToDirection &&
                        belongsToProvider);

                return showMarkAsReadButton;
            })
        );
    };

    const unreadMessagesPresent = checkUnreadMessagesPresent(messages);

    //add more symptoms,if there's no tracking factors,then refresh the page
    if (location?.state?.fromPatientDashboard == true) {
        window.location.reload();
    }
    const handleDayClick = (day, { disabled }) => {
        if (disabled) {
            return;
        }
        setDate(day);
    };

    //console.log("Clinic Name:", patient?.clinic_name, "| Clinic Location:", patient?.clinic_location);
    
    useEffect(() => {
        let isMounted = true; // Add a flag to track if the component is mounted
        // Use the isMounted flag in your asynchronous operation
        getPatientRegistrationProgress(patient?.patientId)
            .then(({ data }) => {
                if (isMounted) {
                    // Check if the component is still mounted before updating state
                    setFirstUnfinishedStep(data);
                }
            })
            .catch((e) => console.log("getPatientRegistrationProgress", e));

        return () => {
            isMounted = false; // Set the flag to false when the component is unmounted
        };
    }, [patient]);

    if (patient && firstUnfinishedStep.firstFalseStep != "finished") {
        if (firstUnfinishedStep.firstFalseStep == "step1") {
            history.push("/BasicInfo");
        }
        if (
            firstUnfinishedStep.firstFalseStep == "step2" ||
            firstUnfinishedStep.firstFalseStep == "step3" ||
            firstUnfinishedStep.firstFalseStep == "step4"
        ) {
            history.push("/laterSymptoms", {
                fromPatientControl: true, // add this to skip step7b-Notification preference
            });
        }
        if (firstUnfinishedStep.firstFalseStep == "step5") {
            history.push("/laterSymptoms", {
                fromPatientControl: true, // add this to skip step7b-Notification preference
            }); //do not push to selectTopSymptoms because it needs info from step4
        }
        if (firstUnfinishedStep.firstFalseStep == "step6") {
            history.push("/selectTrigger", {
                fromPatientControl: true, // add this to skip step7b-Notification preference
            });
        }
        if (firstUnfinishedStep.firstFalseStep == "step7") {
            history.push("/notificationPrefs", {
                fromPatientControl: true, // add this to skip step7b-Notification preference
            });
        }
    }

    useEffect(() => {
        let isMounted = true; // Add a flag to track if the component is mounted
        // Use the isMounted flag in your asynchronous operation
        getAdditionalNote(patient?.patientId)
            .then(({ data }) => {
                if (isMounted) {
                    // Check if the component is still mounted before updating state
                    setAdditionalNotes(data);
                }
            })
            .catch((e) => console.log("getAdditionalNotesError", e));

        return () => {
            isMounted = false; // Set the flag to false when the component is unmounted
        };
    }, [patient]);

    useEffect(() => {
        let isMounted = true; // add this variable to keep track of component's mounted status
        if (topSymptoms && topSymptoms.length > 0 && trackedData) {
            const loggedSymptomToday = getLoggedDataToday(
                trackedData,
                "medical",
                date
            );
            if (loggedSymptomToday.length === 0 && isMounted) {
                // check if component is still mounted before updating state
                setCurrentTrackingSymptom(topSymptoms[0]);
                setCurrentTrackingStep(0);
                setSkippedUnloggedSymptoms(0);
            } else if (isMounted) {
                // check if component is still mounted before calling saveTrackingStep()
                saveTrackingStep();
            }
        }
        // Cleanup function to handle unmounting of the component
        return () => {
            isMounted = false; // update the isMounted variable to reflect that component is unmounted
        };
    }, [history, topSymptoms, trackedData, date]);

    useEffect(() => {
        // TODO: This is a bandaid for resetting on date change, this should be removed in the future. (When doing so, re-add date to the previous useEffect)
        if (topSymptoms && topSymptoms.length > 0 && trackedData) {
            setCurrentTrackingSymptom(topSymptoms[0]);
            setCurrentTrackingStep(0);
            setSkippedUnloggedSymptoms(0);
            prepareNextSymptomTrackingStep(0);
        }
    }, [date]);

    useEffect(() => {
        if (
            (user.userType == "caregiver" || user.userType == "provider") &&
            !patient
        ) {
            history.push("/patientControl");
        }
    }, []);

    function sqlToJsDate(sqlDate) {
        // Example value: 2025-02-18T05:00:00.000Z
        var t = sqlDate.split(/[-T:.Z]/);
        return new Date(
            Number(t[0]),
            Number(t[1]) - 1,
            Number(t[2]),
            0,
            0,
            0,
            0
        );
    }

    // Get tracked days for calendar.
    useEffect(() => {
        if (!patient) {
            console.log('Debug - patient is null, skipping tracked days fetch');
            return;
        }
        //console.log('Debug - patientID being passed:', patient.patientId);
        getPartiallyTrackedDays({ patientId: patient.patientId }).then(
            ({ data }) => {
                let tempTrackedDays = data.map((d) =>
                    sqlToJsDate(d.symptom_date)
                );
                setPartiallyTrackedDays(tempTrackedDays);

                getTrackedDays({ patientId: patient.patientId }).then(
                    ({ data }) => {
                        const dates = data.map((d) =>
                            sqlToJsDate(d.symptom_date)
                        );
                        setTrackedDays(dates);
                        tempTrackedDays = tempTrackedDays.concat(dates);

                        tempTrackedDays.sort(
                            (a, b) => a.getTime() - b.getTime()
                        );
                        let end = new Date();
                        end.setHours(0);
                        end.setMinutes(0);
                        end.setSeconds(0);
                        let start = new Date(
                            end.getFullYear(),
                            end.getMonth(),
                            1
                        );
                        if (
                            tempTrackedDays.length > 0 &&
                            tempTrackedDays[0].getTime() < start.getTime()
                        ) {
                            start = tempTrackedDays[0];
                        }

                        let i = 0;
                        const tempUntrackedDays = [];
                        for (
                            const dt = new Date(start);
                            dt < end;
                            dt.setDate(dt.getDate() + 1)
                        ) {
                            if (
                                i < tempTrackedDays.length &&
                                tempTrackedDays[i].getMonth() ==
                                    dt.getMonth() &&
                                tempTrackedDays[i].getDate() == dt.getDate()
                            ) {
                                i += 1;
                                continue;
                            }
                            tempUntrackedDays.push(new Date(dt));
                        }
                        setUntrackedDays(tempUntrackedDays);
                    }
                );
            }
        );
    }, [patient, date]);

    // Fetch assigned exercises for patients and caregivers
    useEffect(() => {
        let isMounted = true;
        
        // Only fetch for patients and caregivers, not providers
        if (user.userType !== "provider" && patient?.patientId) {
            setLoadingExercises(true);
            getPatientExercises(patient.patientId)
                .then(({ data }) => {
                    if (isMounted) {
                        setAssignedExercises(data || {});
                        enqueuePendingCompletions(data || {});
                        setLoadingExercises(false);
                        
                        // Check initial daily limit status for all exercises
                        if (data) {
                            const limitStatus = {};
                            const today = new Date().toISOString().split('T')[0];
                            Object.entries(data).forEach(([categoryOrSlug, value]) => {
                                if (value && typeof value === 'object' && value.exercise_name) {
                                    // Direct exercise at root level
                                    //console.log(`[getPatientExerciseList] Exercise "${value.exercise_name}" - num_logs: ${value.num_logs}, date_last_logged: ${value.date_last_logged}`);
                                    if (value.num_logs >= MAX_INCREMENTS_PER_DAY && value.date_last_logged?.startsWith(today)) {
                                        limitStatus[value.exercise_id] = true;
                                    }
                                } else if (value && typeof value === 'object') {
                                    // Category with nested exercises
                                    Object.entries(value).forEach(([slug, exercise]) => {
                                        if (exercise && exercise.exercise_name) {
                                            //console.log(`[getPatientExerciseList] Exercise "${exercise.exercise_name}" - num_logs: ${exercise.num_logs}, date_last_logged: ${exercise.date_last_logged}`);
                                        }
                                        if (exercise && exercise.num_logs >= MAX_INCREMENTS_PER_DAY && exercise.date_last_logged?.startsWith(today)) {
                                            limitStatus[exercise.exercise_id] = true;
                                        }
                                    });
                                }
                            });
                            setExerciseDailyLimitReached(limitStatus);
                        }
                    }
                })
                .catch((error) => {
                    console.error("Error fetching assigned exercises:", error);
                    if (isMounted) {
                        setLoadingExercises(false);
                    }
                });
        }

        return () => {
            isMounted = false;
        };
    }, [user.userType, patient?.patientId]);

    // Fetch assigned exercises for provider to view what patient has
    useEffect(() => {
        let isMounted = true;
        
        // Only fetch for providers when a patient is selected
        if (user.userType === "provider" && patient?.patientId) {
            setLoadingProviderViewExercises(true);
            
            // Pass patientId to get THAT patient's exercises
            getPatientExercises(patient.patientId)
                .then(({ data }) => {
                    if (isMounted) {
                        console.log("Provider received patient exercises:", data);
                        setProviderViewAssignedExercises(data || {});
                        setLoadingProviderViewExercises(false);
                        setExerciseChangeAlertDismissed(false);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching patient's assigned exercises:", error);
                    if (isMounted) {
                        setProviderViewAssignedExercises({});
                        setLoadingProviderViewExercises(false);
                    }
                });
        } else if (user.userType === "provider" && !patient?.patientId) {
            // Clear when no patient selected
            setProviderViewAssignedExercises({});
            setLoadingProviderViewExercises(false);
        }

        return () => {
            isMounted = false;
        };
    }, [user.userType, patient?.patientId]);

    // Check if all assigned exercises (including +2 levels) are completed at 5/5
    useEffect(() => {
        // Only check for patients/caregivers, not providers
        if (user.userType === "provider" || loadingExercises || appointmentEmailSent) {
            return;
        }

        // Helper function to get exercise slug and level from video ID
        const getExerciseFromVideoId = (videoId) => {
            const lowerVideoId = videoId.toLowerCase();
            
            const patterns = [
                { patterns: ['pg', 'palmergrasp'], slug: 'palmergrasp' },
                { patterns: ['root', 'rooting'], slug: 'rooting' },
                { patterns: ['snout'], slug: 'snout' },
                { patterns: ['bab', 'babinski'], slug: 'babinski' },
                { patterns: ['sg', 'spinalgalant', 'spinal'], slug: 'spinalgalant' },
                { patterns: ['atnr'], slug: 'atnr' },
                { patterns: ['stnr'], slug: 'stnr' },
                { patterns: ['moro'], slug: 'moro' },
                { patterns: ['tlr'], slug: 'tlr' },
                { patterns: ['balance', 'bal'], slug: 'balance' },
                { patterns: ['curlups', 'curl', '-cu-', '-cu'], slug: 'curlups' },
                { patterns: ['lateralsideplank', 'lateral', 'sideplank', '-lsp-', '-lsp'], slug: 'lateralsideplank' },
                { patterns: ['pronebridge', 'prone', '-pb-', '-pb'], slug: 'pronebridge' },
                { patterns: ['pullups', '-pu-', '-pu', 'pull'], slug: 'pullups' },
                { patterns: ['pushups', '-psh-', '-psh', 'push'], slug: 'pushups' },
                { patterns: ['supinebridge', 'supine', '-sb-', '-sb'], slug: 'supinebridge' },
                { patterns: ['oculomotor', 'ocular', 'ocul'], slug: 'oculomotor' },
                { patterns: ['olfactory', 'olfac'], slug: 'olfactory' },
                { patterns: ['vestibular', 'vest'], slug: 'vestibular' },
                { patterns: ['interactivemetronome', 'interactive', 'metronome'], slug: 'interactivemetronome' },
                { patterns: ['metrotimer', 'metro'], slug: 'metrotimer' }
            ];
            
            let foundSlug = null;
            
            for (const { patterns: patternList, slug } of patterns) {
                for (const pattern of patternList) {
                    if (lowerVideoId.includes(pattern)) {
                        foundSlug = slug;
                        break;
                    }
                }
                if (foundSlug) break;
            }
            
            if (!foundSlug) {
                return null;
            }
            
            const levelMatch = videoId.match(/level-(\d+)|l(\d+)|-(\d+)(?:-|$)/i);
            const level = levelMatch ? parseInt(levelMatch[1] || levelMatch[2] || levelMatch[3]) : null;
            
            return { slug: foundSlug, level };
        };

        // Build list of all required video IDs (assigned level + 2 levels after)
        const requiredVideoIds = [];
        
        Object.entries(assignedExercises).forEach(([categoryOrSlug, value]) => {
            if (value && typeof value === 'object') {
                const processExercise = (slug, exercise) => {
                    const assignedLevel = exercise.exercise_level;
                    
                    // For each assigned exercise, we need levels: assigned, assigned+1, assigned+2
                    for (let levelOffset = 0; levelOffset <= 2; levelOffset++) {
                        const targetLevel = assignedLevel + levelOffset;
                        
                        // Find all video IDs that match this slug and level
                        Object.keys(videoCounts).forEach(videoId => {
                            const exerciseInfo = getExerciseFromVideoId(videoId);
                            if (exerciseInfo && 
                                exerciseInfo.slug === slug && 
                                exerciseInfo.level === targetLevel) {
                                requiredVideoIds.push(videoId);
                            }
                        });
                    }
                };
                
                if (value.exercise_name) {
                    // Direct exercise at root
                    processExercise(categoryOrSlug, value);
                } else {
                    // Category with nested exercises
                    Object.entries(value).forEach(([slug, exercise]) => {
                        processExercise(slug, exercise);
                    });
                }
            }
        });

        // Check if we have any required videos
        if (requiredVideoIds.length === 0) {
            return; // No exercises assigned yet
        }

        // Check if ALL required videos have count = 5
        const allCompleted = requiredVideoIds.every(videoId => {
            return videoCounts[videoId] === 5;
        });

        console.log("Appointment Email Check:", {
            requiredVideoIds,
            videoCounts,
            allCompleted,
            appointmentEmailSent
        });

        // If all completed and email not sent yet, send it
        if (allCompleted && !appointmentEmailSent) {
            const emailData = {
                patientId: patient?.patientId,
                userEmail: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                userType: user.userType
            };

            scheduleAppointmentEmail(emailData)
                .then(() => {
                    console.log("Appointment email sent successfully");
                    setAppointmentEmailSent(true);
                    
                    // Show completion modal to user
                    setShowCompletionModal(true);
                })
                .catch((error) => {
                    console.error("Error sending appointment email:", error);
                });
        }
    }, [assignedExercises, videoCounts, loadingExercises, appointmentEmailSent, user, patient]);

    function prepareNextSymptomTrackingStep(skipped) {
        const loggedSymptomToday = getLoggedDataToday(
            trackedData,
            "medical",
            date
        );

        const unloggedSymptoms = topSymptoms.filter((topSymptom) => {
            return !loggedSymptomToday.some((loggedSymptom) => {
                return loggedSymptom.factor === topSymptom.factor;
            });
        });
        const numLoggedSymptoms = topSymptoms.length - unloggedSymptoms.length;

        setSkippedUnloggedSymptoms(skipped);
        setCurrentTrackingStep(numLoggedSymptoms + skipped);

        const nextSymptom = unloggedSymptoms[skipped];
        if (!nextSymptom) {
            finishTrackingStep();
        } else {
            setCurrentTrackingSymptom(nextSymptom);
        }
    }

    const finishTrackingStep = () => {
        const loggedTriggerToday = getLoggedDataToday(
            trackedData,
            "SDOH",
            date
        );
        if (loggedTriggerToday.length || currentTrackingSymptom === "SDOH") {
            setCurrentTrackingSymptom("done");
        } else {
            setCurrentTrackingSymptom("SDOH");
        }
    };

    const skipTrackingStep = () => {
        prepareNextSymptomTrackingStep(skippedUnloggedSymptoms + 1);
    };

    const saveTrackingStep = () => {
        prepareNextSymptomTrackingStep(skippedUnloggedSymptoms);
    };

    // Extracting properties from the user object
    const { userType } = user;

    const ConditionalFeelingToday =
        status != "Released" &&
        (userType == "patient" || userType == "caregiver") &&
        (loadingFeelingToday ? (
            <LoadingSpinner />
        ) : (
            !doneSimpleTracking && (
                <FeelingToday
                    setDoneSimpleTracking={setDoneSimpleTracking}
                    date={date}
                />
            )
        ));

    const ConditionalSymptomTracker =
        status != "Released" &&
        (userType == "patient" || userType == "caregiver") &&
        (loadingTopSymptoms || loadingTrackedData || loadingTopTrigger ? (
            <LoadingSpinner />
        ) : (
            <>
                {currentTrackingSymptom === "SDOH" && (
                    <ComponentWrapper>
                        <TriggerSelector
                            topTrigger={topTrigger}
                            userType={userType}
                            errorLoadingTopTrigger={errorLoadingTopTrigger}
                            loadTrigger={loadTrigger}
                            date={date}
                            skipTrackingStep={skipTrackingStep}
                            saveTrackingStep={saveTrackingStep}
                            loadTrackedData={loadTrackedData}
                        />
                    </ComponentWrapper>
                )}
                {currentTrackingSymptom !== "SDOH" &&
                    currentTrackingSymptom !== "done" && (
                        <ComponentWrapper>
                            <SymptomPicker
                                currentTrackingStep={currentTrackingStep}
                                topSymptoms={topSymptoms}
                                date={date}
                                user={user}
                                symptom={currentTrackingSymptom}
                                trackedData={trackedData}
                                skipTrackingStep={skipTrackingStep}
                                saveTrackingStep={saveTrackingStep}
                                loadTrackedData={loadTrackedData}
                            />
                        </ComponentWrapper>
                    )}
                {currentTrackingSymptom === "done" && (
                    <ComponentWrapper>
                        <AdditionalNote date={date} />
                    </ComponentWrapper>
                )}
            </>
        ));

    const ConditionalAddSymptoms = status != "Released" &&
        (userType == "patient" || userType == "caregiver") && (
            <AddSymptoms
                topSymptoms={topSymptoms}
                setTopSymptoms={setTopSymptoms}
            />
        );

    const ConditionalCombinedChart =
        loadingTrackedData || loadingTopSymptoms ? (
            <LoadingSpinner />
        ) : (
            <Charts
                topSymptoms={topSymptoms}
                data={trackedData}
                error={errorLoadingTrackedData}
                retry={loadTrackedData}
                additionalNotes={additionalNotes}
                user={user}
                patient={patient}
            />
        );

    const ConditionalCurrentPatient = () =>
        (userType === "caregiver" || userType === "provider") && (
            <Col md={3}>
                <CurrentPatient userType={userType} />
            </Col>
        );

    const ConditionalOnOffButtonMessages = () =>
        userType === "provider" && (
            <OnOffButtonMessages
                onTitle={t("dashboard.toggleMessages.on")}
                offTitle={t("dashboard.toggleMessages.off")}
                caregiverList={caregiverList}
                onOffButtonMessagesStatus={onOffButtonMessagesStatus}
                setOnOffButtonMessagesStatus={setOnOffButtonMessagesStatus}
            />
        );

    const ConditionalOnOffButtonAlert = () =>
        userType === "provider" && (
            <OnOffButtonAlert
                onTitle={t("dashboard.toggleAlerts.on")}
                offTitle={t("dashboard.toggleAlerts.off")}
            />
        );

    const ConditionalPatientStatus = () =>
        userType === "provider" && (
            <PatientStatus
                status={status}
                setStatus={setStatus}
                userType={userType}
                patientId={patient?.patientId}
                patientList={patientList}
                caregiverList={caregiverList}
                setCaregiverList={setCaregiverList}
                setPatientList={setPatientList}
            />
        );

    const ConditionalCalendar = () => {
        return (
            <Calendar
                selectedDays={date}
                onDayClick={handleDayClick}
                modifiers={{
                    tracked: trackedDays,
                    partially: partiallyTrackedDays,
                    untracked: untrackedDays,
                }}
            />
        );
    };

    const ConditionalSendMessage = () => (
        <Col md={3}>
            <div className="d-flex justify-content-center">
                <h6
                    className="pt-2 pb-2 pl-3 pr-3"
                    style={{
                        width: "75%",
                        height: "100%",
                        borderRadius: "20px",
                        backgroundColor: dark
                            ? theme.darkModePrimary
                            : unreadMessagesPresent
                            ? "#3EB1A6"
                            : "#ef5866",
                        color: dark ? theme.darkModeText : "white",
                        textAlign: "center",
                        overflowWrap: "break-word",
                        cursor: "pointer",
                    }}
                    onClick={() =>
                        history.push("/messages", {
                            curPatient: patient,
                        })
                    }
                >
                    {unreadMessagesPresent ? (
                        <div>
                            {t("dashboard.mailbox.new")}
                            <img
                                style={{
                                    maxHeight: "30px",
                                    maxWidth: "30px",
                                    display: "inline-block",
                                    paddingLeft: 10,
                                }}
                                src={emailAlert}
                                alt="email alert"
                            />
                        </div>
                    ) : (
                        t("dashboard.mailbox.sendOrRead")
                    )}
                </h6>
            </div>
        </Col>
    );
    const ConditionalAssignmentButton = () => {
        if (user.userType === "provider") {
            return (
                <div className="mb-4 mt-3">
                    <AssignmentButton
                        userId={user.userId}
                        patientId={patient?.patientId}
                        clinicLocation={patient?.clinic_location}
                    />
                </div>
            );
        }
    };

    const ConditionalHemisphericSum = () => {
        if (user.userType === "provider") {
            return (
                <div>
                    <HemisphericSum patientId={patient?.patientId} />
                </div>
            );
        }
    };

    // Conditional component for Selected Exercises (providers only)
    const ConditionalSelectedExercises = () => {
        if (user.userType === "provider" && showAssignmentDropdown) {
            // Helper function to translate a stored displayName to the current language
            const translateDisplayName = (displayName) => {
                // displayName format: "Exercise Name - <level word> X" (level word is in
                // whatever language was active when it was selected, e.g. Level/Nivel/级别)
                const match = displayName.match(/^(.+?)\s*-\s*\S+\s*(\d+)$/);
                
                if (match) {
                    const exerciseName = match[1].trim();
                    const level = match[2];
                    
                    let translatedName = exerciseName;
                    
                    // Try direct lookup first
                    if (tExerciseName(exerciseName)) {
                        translatedName = tExerciseName(exerciseName);
                    } else {
                        // Try variations - remove common suffixes/words
                        const variations = [
                            exerciseName,
                            exerciseName.replace(/\s+(Treatment|Exercises|Exercise)$/i, ''),
                            exerciseName.replace(/\s+Treatment$/i, ''),
                            exerciseName.replace(/\s+Exercises$/i, ''),
                            exerciseName.replace(/\s+Exercise$/i, '')
                        ];
                        
                        // Try each variation
                        for (const variation of variations) {
                            if (tExerciseName(variation)) {
                                translatedName = tExerciseName(variation);
                                break;
                            }
                        }
                    }
                    
                    return `${translatedName} - ${levelWord} ${level}`;
                }
                
                // If pattern doesn't match, return as-is
                return displayName;
            };

            const handleAssignVideos = () => {
                setShowConfirmModal(true);
            };

            const selectedCheckboxes = Object.keys(selectedVideos).filter(id => selectedVideos[id]);
            const totalSelections = selectedExercisesForAssignment.length + selectedCheckboxes.length;

            return (
                <Card className="mb-3 mt-3">
                    <Card.Body>
                        <h5 className="mb-3">{t("dashboard.selectedExercises.title")}</h5>
                        <p className="text-muted">
                            {t("dashboard.selectedExercises.youHaveSelected")} <strong>{totalSelections}</strong> {t("dashboard.selectedExercises.itemsForAssignment")}
                        </p>
                        {totalSelections > 0 ? (
                            <>
                                <div className="mb-3">
                                    <h6>{t("dashboard.selectedExercises.selectedLabel")}</h6>
                                    <ul style={{ listStyle: 'none', padding: 0 }}>
                                        {/* Level-based exercises */}
                                        {selectedExercisesForAssignment.map((exercise, index) => (
                                            <li key={`level-${index}`} style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                padding: '8px 12px',
                                                marginBottom: '8px',
                                                backgroundColor: dark ? '#2d2d2d' : '#f8f9fa',
                                                borderRadius: '6px',
                                                border: `1px solid ${dark ? '#444' : '#dee2e6'}`
                                            }}>
                                                <span>{translateDisplayName(exercise.displayName)}</span>
                                                <button
                                                    onClick={() => {
                                                        setSelectedExercisesForAssignment(prev => 
                                                            prev.filter((_, i) => i !== index)
                                                        );
                                                    }}
                                                    style={{
                                                        padding: '4px 12px',
                                                        backgroundColor: '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                        fontWeight: '500'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.target.style.backgroundColor = '#c82333';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.target.style.backgroundColor = '#dc3545';
                                                    }}
                                                >
                                                    {t("dashboard.selectedExercises.unassignButton")}
                                                </button>
                                            </li>
                                        ))}
                                        
                                        {/* Checkbox-based selections */}
                                        {selectedCheckboxes.map((videoId, index) => (
                                            <li key={`checkbox-${index}`} style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                padding: '8px 12px',
                                                marginBottom: '8px',
                                                backgroundColor: dark ? '#2d2d2d' : '#f8f9fa',
                                                borderRadius: '6px',
                                                border: `1px solid ${dark ? '#444' : '#dee2e6'}`
                                            }}>
                                                <span style={{ fontSize: '14px' }}>{videoId}</span>
                                                <button
                                                    onClick={() => {
                                                        setSelectedVideos(prev => ({
                                                            ...prev,
                                                            [videoId]: false
                                                        }));
                                                    }}
                                                    style={{
                                                        padding: '4px 12px',
                                                        backgroundColor: '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                        fontWeight: '500'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.target.style.backgroundColor = '#c82333';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.target.style.backgroundColor = '#dc3545';
                                                    }}
                                                >
                                                    {t("dashboard.selectedExercises.unassignButton")}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <Button 
                                    variant="primary"
                                    onClick={handleAssignVideos}
                                    style={{
                                        width: '100%',
                                        fontWeight: '600'
                                    }}
                                >
                                    {t("dashboard.selectedExercises.assignButton")}
                                </Button>
                            </>
                        ) : (
                            <p className="text-muted">{t("dashboard.selectedExercises.selectFromList")}</p>
                        )}
                    </Card.Body>
                </Card>
            );
        }
        return null;
    };

    // Currently Assigned Exercises component for providers - mirrors patient's view
    const CurrentlyAssignedExercises = () => {
        if (user.userType !== "provider" || !patient?.patientId) {
            return null;
        }

        // Helper function to map category slugs to display names and colors
        const getCategoryInfo = (categorySlug) => {
            // First, map individual exercise slugs to their parent categories
            const exerciseSlugToCategory = {
                // Core category mapping
                //'core': 'core-exercises',
                
                // Core Exercises
                'curlups': 'core-exercises',
                'lateralsideplank': 'core-exercises',
                'pronebridge': 'core-exercises',
                'pullups': 'core-exercises',
                'pushups': 'core-exercises',
                'supinebridge': 'core-exercises',
                
                // Primitive Reflexes
                'palmergrasp': 'primitive-reflexes',
                'rooting': 'primitive-reflexes',
                'snout': 'primitive-reflexes',
                'babinski': 'primitive-reflexes',
                'spinalgalant': 'primitive-reflexes',
                'atnr': 'primitive-reflexes',
                'stnr': 'primitive-reflexes',
                'moro': 'primitive-reflexes',
                'tlr': 'primitive-reflexes',
                
                // Sensory Equipment (these may already be at category level)
                'transcranial': 'sensory-equipment',
                'pons': 'sensory-equipment',
                'photobiomodulation-left': 'sensory-equipment',
                'photobiomodulation-right': 'sensory-equipment',
            };
            
            // Check if this is an individual exercise slug that needs to be mapped to a category
            const mappedCategory = exerciseSlugToCategory[categorySlug] || categorySlug;
            
            const categoryMap = {
                'primitive-reflexes': { slug: 'primitive-reflexes', color: '#6f42c1' }, // Purple
                'core': { slug: 'core-exercises', color: '#007bff' }, // Blue - maps 'core' to 'core-exercises'
                'core-exercises': { slug: 'core-exercises', color: '#007bff' }, // Blue
                'balance': { slug: 'balance', color: '#28a745' }, // Green
                'oculomotor': { slug: 'oculomotor', color: '#17a2b8' }, // Cyan
                'vestibular': { slug: 'vestibular', color: '#ffc107' }, // Yellow/Gold
                'olfactory': { slug: 'olfactory', color: '#fd7e14' }, // Orange
                'rhythm-timing': { slug: 'rhythm-timing', color: '#e83e8c' }, // Pink
                'sensory-equipment': { slug: 'sensory-equipment', color: '#6610f2' }, // Indigo
                'metrotimer': { slug: 'metrotimer', color: '#20c997' }, // Teal
                'interactivemetronome': { slug: 'interactivemetronome', color: '#d63384' } // Dark Pink
            };
            
            if (categoryMap[mappedCategory]) {
                return categoryMap[mappedCategory];
            }
            
            // Format unknown categories: convert slug to title case
            //const formattedName = categorySlug
            //    .split('-')
            //    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            //    .join(' ');
            
            return { slug: categorySlug, color: '#6c757d' };
        };
        
        // Helper function to get translated category name from slug
        const getTranslatedCategoryName = (categorySlug) => {
            // MANUAL TRANSLATIONS: Handle specific categories that aren't translating properly
            // Maps slug/name variants to a translation key under dashboard.ui.exerciseCategory
            const manualTranslations = {
                'core': 'coreExercises',
                'Core': 'coreExercises',
                'sensory': 'sensoryEquipment',
                'Sensory': 'sensoryEquipment',
                'sensory-equipment': 'sensoryEquipment',
                'primitive-reflex': 'primitiveReflex',
                'Primitive Reflex': 'primitiveReflex',
                'rhythm-timing': 'rhythmAndTiming',
                'rhythm-and-timing': 'rhythmAndTiming',
                'Rhythm And Timing': 'rhythmAndTiming'
            };
            
            if (manualTranslations[categorySlug]) {
                return t(`dashboard.ui.exerciseCategory.${manualTranslations[categorySlug]}`);
            }
            
            if (tExerciseCategory(categorySlug)) {
                return tExerciseCategory(categorySlug);
            }
            
            // Format unknown categories: convert slug to title case
            const formatted = categorySlug
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            
            // MANUAL FIX: Check the formatted result against our manual translations
            if (manualTranslations[formatted]) {
                return t(`dashboard.ui.exerciseCategory.${manualTranslations[formatted]}`);
            }
            
            return formatted;
        };

        // Helper function to translate exercise names
        const translateExerciseName = (exerciseName) => {
            // Try to find direct translation in EXERCISE_NAMES
            if (tExerciseName(exerciseName)) {
                return tExerciseName(exerciseName);
            }
            // If no translation found, return original name
            return exerciseName;
        };

        // Navigate to Reports page with selected category
        const handleCategoryClick = (categoryName) => {
            history.push({
                pathname: '/charts',
                state: { 
                    days: 30, // default days
                    expandedCategory: categoryName // pass the category to expand
                }
            });
        };

        // Open Update Exercise modal
        const handleOpenUpdateModal = () => {
            // Prepare exercises list for modal
            const exercisesList = [];
            Object.entries(providerViewAssignedExercises).forEach(([categoryOrSlug, value]) => {
                if (value && typeof value === 'object') {
                    if (value.exercise_name) {
                        const categoryInfo = getCategoryInfo(categoryOrSlug);
                        exercisesList.push({
                            slug: categoryOrSlug,
                            name: value.exercise_name,
                            level: value.exercise_level,
                            categorySlug: categoryInfo.slug,
                            progress: value.progress || 0,
                            previousLevel: value.level_since_last_eval ?? null,
                        });
                    } else {
                        Object.entries(value).forEach(([slug, exercise]) => {
                            if (exercise && exercise.exercise_name) {
                                const categoryInfo = getCategoryInfo(categoryOrSlug);
                                exercisesList.push({
                                    slug: slug,
                                    name: exercise.exercise_name,
                                    level: exercise.exercise_level,
                                    categorySlug: categoryInfo.slug,
                                    progress: exercise.progress || 0,
                                    previousLevel: exercise.level_since_last_eval ?? null,
                                });
                            }
                        });
                    }
                }
            });
            setExercisesToUpdate(exercisesList.map(ex => ({ ...ex, level: String(ex.level) })));
            setOriginalExercises(exercisesList.map(ex => ({ ...ex, level: String(ex.level) }))); // Deep copy, levels as strings to match input state
            setShowUpdateModal(true);
        };

        // Handle level change in modal
        const handleLevelChange = (index, newLevel) => {
            // Store as raw string while typing — avoids empty string coercing to 0
            // If level has changed from the originally loaded value, reset displayed progress to 0
            const originalLevel = String(originalExercises[index]?.level);
            const updated = exercisesToUpdate.map((ex, i) =>
                i === index
                    ? { ...ex, level: newLevel, progress: newLevel !== originalLevel ? 0 : originalExercises[index]?.progress }
                    : ex
            );
            setExercisesToUpdate(updated);
        };

        // Handle exercise deletion
        const handleDeleteExercise = (index) => {
            const updated = exercisesToUpdate.filter((_, i) => i !== index);
            setExercisesToUpdate(updated);
        };

        // Handle undo - reset to original state
        const handleUndoChanges = () => {
            setExercisesToUpdate(originalExercises.map(ex => ({ ...ex })));
        };

        // Detect whether the provider has made any changes in the modal
        const hasChanges = (() => {
            // Different number of exercises means something was deleted
            if (exercisesToUpdate.length !== originalExercises.length) return true;
            // Same length — check if any exercise level was changed
            return exercisesToUpdate.some((ex, i) => {
                const orig = originalExercises[i];
                return (
                    ex.slug !== orig.slug ||
                    String(ex.level).trim() !== String(orig.level).trim()
                );
            });
        })();

        // Save updated exercises
        const handleSaveUpdates = async () => {
            setUpdatingExercises(true);
            try {
                // Detect which exercises were deleted
                const currentSlugs = new Set(exercisesToUpdate.map(ex => ex.slug));
                const deletedExercises = originalExercises
                    .filter(ex => !currentSlugs.has(ex.slug))
                    .map(ex => ex.slug);

                // First, remove deleted exercises if any
                if (deletedExercises.length > 0) {
                    const removeData = {
                        patientId: patient.patientId,
                        exercises: deletedExercises
                    };
                    
                    const removeResponse = await removePatientExercises(removeData);
                    console.log("Removed exercises:", removeResponse.data);
                }

                // Then, update/assign only exercises whose level actually changed
                const changedExercises = exercisesToUpdate.filter(ex => {
                    const orig = originalExercises.find(o => o.slug === ex.slug);
                    return orig && String(ex.level).trim() !== String(orig.level).trim();
                });

                if (changedExercises.length > 0) {
                    // Build exercises object in the format: { slug: level }
                    // API expects: { "babinski": 3, "pushups": 4 }
                    const exercises = {};

                    changedExercises.forEach(exercise => {
                        exercises[exercise.slug] = parseInt(exercise.level);
                    });

                    const assignmentData = {
                        patientId: patient.patientId,
                        exercises: exercises
                    };

                    console.log('Sending assignment data:', JSON.stringify(assignmentData, null, 2));

                    const response = await sendAssignments(assignmentData);
                    console.log("Updated exercises:", response.data);
                }
                
                // Refresh the exercises list
                const { data } = await getPatientExercises(patient.patientId);
                setProviderViewAssignedExercises(data || {});
                setShowUpdateModal(false);
                showAlert(t("dashboard.updateExerciseModal.successMessage"));
                
            } catch (error) {
                console.error("Error updating exercises:", error);
                showAlert(t("dashboard.updateExerciseModal.errorMessage"));
            } finally {
                setUpdatingExercises(false);
            }
        };

        // Organize exercises by category
        const exercisesByCategory = {};

        Object.entries(providerViewAssignedExercises).forEach(([categoryOrSlug, value]) => {
            if (value && typeof value === 'object') {
                if (value.exercise_name) {
                    // Direct exercise at root level - treat it as its own category
                    const categoryInfo = getCategoryInfo(categoryOrSlug);
                    if (!exercisesByCategory[categoryInfo.slug]) {
                        exercisesByCategory[categoryInfo.slug] = { exercises: [], color: categoryInfo.color };
                    }
                    exercisesByCategory[categoryInfo.slug].exercises.push({
                        name: value.exercise_name,
                        level: value.exercise_level,
                        slug: categoryOrSlug,
                        progress: value.progress || 0,
                        previousLevel: value.level_since_last_eval ?? null,
                        exerciseId: value.exercise_id,
                        numLogs: value.num_logs || 0,
                        dateLastLogged: value.date_last_logged ?? null
                    });
                } else {
                    // Category with nested exercises
                    const categoryInfo = getCategoryInfo(categoryOrSlug);
                    if (!exercisesByCategory[categoryInfo.slug]) {
                        exercisesByCategory[categoryInfo.slug] = { exercises: [], color: categoryInfo.color };
                    }
                    
                    Object.entries(value).forEach(([slug, exercise]) => {
                        if (exercise && exercise.exercise_name) {
                            exercisesByCategory[categoryInfo.slug].exercises.push({
                                name: exercise.exercise_name,
                                level: exercise.exercise_level,
                                slug: slug,
                                progress: exercise.progress || 0,
                                previousLevel: exercise.level_since_last_eval ?? null,
                                exerciseId: exercise.exercise_id,
                                numLogs: exercise.num_logs || 0,
                                dateLastLogged: exercise.date_last_logged ?? null
                            });
                        }
                    });
                }
            }
        });

        const totalExercises = Object.values(exercisesByCategory).reduce(
            (sum, cat) => sum + cat.exercises.length, 
            0
        );

        if (loadingProviderViewExercises) {
            return (
                <Card className="mb-3 mt-3" style={{ 
                    borderColor: dark ? '#28a745' : '#28a745',
                    borderWidth: '2px'
                }}>
                    <Card.Body>
                        <h5 className="mb-3" style={{ color: '#28a745' }}>
                            {t("dashboard.currentlyAssignedExercises")}
                        </h5>
                        <p className="text-muted">Loading assigned exercises...</p>
                    </Card.Body>
                </Card>
            );
        }

        return (
            <>
                <ExerciseChangeAlert
                    exercisesByCategory={exercisesByCategory}
                    dismissed={exerciseChangeAlertDismissed}
                    onDismiss={() => setExerciseChangeAlertDismissed(true)}
                    onOpenUpdateModal={handleOpenUpdateModal}
                    patientName={patient?.name}
                />
                <Card className="mb-3 mt-3" style={{ 
                    borderColor: dark ? '#28a745' : '#28a745',
                    borderWidth: '2px'
                }}>
                    <Card.Body>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '1rem'
                        }}>
                            <h5 className="mb-0" style={{ color: '#28a745' }}>
                                {t("dashboard.currentlyAssignedExercises")} ({totalExercises})
                            </h5>
                            {totalExercises > 0 && (
                                <button
                                    onClick={handleOpenUpdateModal}
                                    style={{
                                        padding: '8px 20px',
                                        backgroundColor: '#6f42c1',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(111, 66, 193, 0.3)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#5a32a3';
                                        e.target.style.boxShadow = '0 4px 6px rgba(111, 66, 193, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#6f42c1';
                                        e.target.style.boxShadow = '0 2px 4px rgba(111, 66, 193, 0.3)';
                                    }}
                                >
                                    {t("dashboard.updateExercisesButton")}
                                </button>
                            )}
                        </div>
                        {totalExercises === 0 ? (
                            <p className="text-muted">
                                {t("dashboard.ui.noExercisesAssigned")}
                            </p>
                        ) : (
                            <div style={{ 
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',
                                maxHeight: "400px", 
                                overflowY: "auto" 
                            }}>
                                {Object.entries(exercisesByCategory).map(([categorySlug, categoryData]) => {
                                    return (
                                        <div key={categorySlug}>
                                            {/* Clickable oval button - navigates to Reports page */}
                                            <button
                                                onClick={() => handleCategoryClick(getTranslatedCategoryName(categorySlug))}
                                                style={{
                                                    display: 'inline-block',
                                                    backgroundColor: dark ? '#444' : '#f8f9fa',
                                                    color: dark ? '#fff' : '#333',
                                                    padding: '10px 24px',
                                                    borderRadius: '25px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    border: `1px solid ${dark ? '#666' : '#dee2e6'}`,
                                                    cursor: 'pointer',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.transform = 'translateY(-2px)';
                                                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.transform = 'translateY(0)';
                                                    e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                                                }}
                                            >
                                                {getTranslatedCategoryName(categorySlug)} ({categoryData.exercises.length})
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* Update Exercise Modal */}
                <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} size="lg" centered>
                    <Modal.Header closeButton style={{ 
                        backgroundColor: dark ? '#2d2d2d' : '#fff',
                        borderBottom: `1px solid ${dark ? '#444' : '#dee2e6'}`
                    }}>
                        <Modal.Title style={{ color: dark ? '#fff' : '#333' }}>
                            {t("dashboard.updateExerciseModal.title")}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ 
                        backgroundColor: dark ? '#1f1f1f' : '#fff',
                        maxHeight: '500px',
                        overflowY: 'auto'
                    }}>
                        {exercisesToUpdate.length === 0 ? (
                            <p className="text-muted">{t("dashboard.updateExerciseModal.noExercises")}</p>
                        ) : (
                            <div>
                                {exercisesToUpdate.map((exercise, index) => (
                                    <div 
                                        key={index}
                                        style={{
                                            padding: '12px',
                                            marginBottom: '12px',
                                            backgroundColor: dark ? '#2d2d2d' : '#f8f9fa',
                                            borderRadius: '8px',
                                            border: `1px solid ${dark ? '#444' : '#dee2e6'}`
                                        }}
                                    >
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '12px'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ 
                                                    fontWeight: '600',
                                                    color: dark ? '#fff' : '#333',
                                                    marginBottom: '4px'
                                                }}>
                                                    {translateExerciseName(exercise.name)}
                                                </div>
                                                <div style={{ 
                                                    fontSize: '12px',
                                                    color: dark ? '#aaa' : '#666'
                                                }}>
                                                    {t("dashboard.updateExerciseModal.category")}: {getTranslatedCategoryName(exercise.categorySlug)}
                                                </div>
                                                <div style={{
                                                    marginTop: '6px',
                                                    fontSize: '12px',
                                                    color: dark ? '#ccc' : '#444',
                                                    display: 'flex',
                                                    gap: '12px',
                                                    flexWrap: 'wrap'
                                                }}>
                                                    {exercise.previousLevel !== null && exercise.previousLevel !== undefined && (
                                                        <span>
                                                            {t("dashboard.updateExerciseModal.originalLevel")}: <strong>{exercise.previousLevel}</strong>
                                                        </span>
                                                    )}
                                                    <span>
                                                        {t("dashboard.updateExerciseModal.currentLevel")}: <strong>{exercise.level}</strong>
                                                    </span>
                                                    {exercise.slug !== 'transcranial' && (
                                                        <span>
                                                            {t("dashboard.updateExerciseModal.currentProgress")}: <strong>{Math.abs(exercise.progress || 0)}/5</strong>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}>
                                                {exercise.previousLevel !== null && exercise.previousLevel !== undefined && exercise.slug !== 'transcranial' && exercise.previousLevel + 2 <= 10 && parseInt(exercise.level, 10) < 10 && (
                                                    <div>
                                                        <div style={{ fontSize: '12px', fontWeight: '600', color: dark ? '#aaa' : '#666', marginBottom: '4px' }}>
                                                            &nbsp;
                                                        </div>
                                                        {(() => {
                                                            const isUnlocked =
                                                                parseInt(exercise.level, 10) === exercise.previousLevel + 1 &&
                                                                Math.abs(exercise.progress || 0) === 5;
                                                            return (
                                                                <button
                                                                    onClick={isUnlocked ? () => handleLevelChange(index, String(exercise.previousLevel + 2)) : undefined}
                                                                    disabled={!isUnlocked}
                                                                    title={!isUnlocked ? `Available when Current Level is ${exercise.previousLevel + 1} and Progress is 5/5` : ''}
                                                                    style={{
                                                                        padding: '6px 12px',
                                                                        backgroundColor: isUnlocked ? '#6f42c1' : '#b0b0b0',
                                                                        color: '#fff',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        fontSize: '12px',
                                                                        fontWeight: '700',
                                                                        cursor: isUnlocked ? 'pointer' : 'not-allowed',
                                                                        whiteSpace: 'nowrap',
                                                                        opacity: isUnlocked ? 1 : 0.6,
                                                                        transition: 'background-color 0.2s, opacity 0.2s'
                                                                    }}
                                                                    onMouseEnter={isUnlocked ? (e) => e.target.style.backgroundColor = '#5a32a3' : undefined}
                                                                    onMouseLeave={isUnlocked ? (e) => e.target.style.backgroundColor = '#6f42c1' : undefined}
                                                                >
                                                                    {t("dashboard.updateExerciseModal.assignNextLevel")} {exercise.previousLevel + 2}
                                                                </button>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                                {parseInt(exercise.level, 10) >= 10 && Math.abs(exercise.progress || 0) >= 5 && exercise.slug !== 'transcranial' && (
                                                    <div style={{
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        color: '#28a745',
                                                        backgroundColor: 'rgba(40, 167, 69, 0.08)',
                                                        border: '1px solid rgba(40, 167, 69, 0.3)',
                                                        borderRadius: '6px',
                                                        padding: '6px 10px',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {t("dashboard.updateExerciseModal.levelMaxReached")}
                                                    </div>
                                                )}
                                                <div>
                                                    <label 
                                                        style={{ 
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            color: dark ? '#aaa' : '#666',
                                                            marginBottom: '4px',
                                                            display: 'block'
                                                        }}
                                                    >
                                                        {t("dashboard.updateExerciseModal.level")}:
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="20"
                                                        value={exercise.level}
                                                        onChange={(e) => handleLevelChange(index, e.target.value)}
                                                        style={{
                                                            width: '70px',
                                                            padding: '6px 10px',
                                                            fontSize: '14px',
                                                            border: `1px solid ${dark ? '#555' : '#ced4da'}`,
                                                            borderRadius: '4px',
                                                            backgroundColor: dark ? '#1f1f1f' : '#fff',
                                                            color: dark ? '#fff' : '#333'
                                                        }}
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteExercise(index)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        backgroundColor: '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                                                >
                                                    {t("dashboard.updateExerciseModal.deleteButton")}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer style={{ 
                        backgroundColor: dark ? '#2d2d2d' : '#fff',
                        borderTop: `1px solid ${dark ? '#444' : '#dee2e6'}`
                    }}>
                        <Button 
                            variant="secondary" 
                            onClick={() => setShowUpdateModal(false)}
                            disabled={updatingExercises}
                        >
                            {t("dashboard.updateExerciseModal.cancelButton")}
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={handleUndoChanges}
                            disabled={updatingExercises || !hasChanges}
                            style={{
                                backgroundColor: '#ffc107',
                                border: 'none',
                                color: '#000',
                                opacity: hasChanges ? 1 : 0.6,
                                cursor: hasChanges ? 'pointer' : 'not-allowed'
                            }}
                        >
                            {t("dashboard.updateExerciseModal.undoButton")}
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleSaveUpdates}
                            disabled={updatingExercises || !hasChanges}
                            style={{
                                backgroundColor: hasChanges ? '#6f42c1' : undefined,
                                border: 'none',
                                opacity: hasChanges ? 1 : 0.6,
                                cursor: hasChanges ? 'pointer' : 'not-allowed'
                            }}
                        >
                            {updatingExercises ? t("dashboard.updateExerciseModal.savingButton") : t("dashboard.updateExerciseModal.saveButton")}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    };

    // const ConditionalTherapyTracker = () => {
    //    return (
    //        <div className="mt-3">
    //            <TherapyTracker
    //                userType={userType}
    //                patientId={patient?.patientId}
    //                date={date}
    //            />
    //        </div>
    //    );
    // };

    // const ConditionalVideoTracker = () => {
    //     return (
    //         <div>
    //             <VideoTracker
    //                 userType={userType}
    //                 patientId={patient?.patientId}
    //                 date={date}
    //             />
    //         </div>
    //     );
    // };

    // Helper function to map exercise to video ID
    const getVideoIdForExercise = (exercise) => {
        const level = exercise.level_since_last_eval ?? exercise.exercise_level;
        const slug = exercise.slug;

        // Primitive reflex subgroup IDs use 'pr-levels-' for 1-3 and 'pr-level-' for 4-10
        const prGroup = (lvl) => lvl <= 3 ? `pr-levels-${lvl}` : `pr-level-${lvl}`;

        // Rooting & Snout merge into a single item at level 7+
        const prRootSnoutItem = (lvl) => lvl >= 7 ? `root-snout-${lvl}` : `root-${lvl}`;
        const prSnoutItem = (lvl) => lvl >= 7 ? `root-snout-${lvl}` : `snout-${lvl}`;

        const videoMap = {
            'palmergrasp':        { group: prGroup(level),            item: `pg-${level}` },
            'rooting':            { group: prGroup(level),            item: prRootSnoutItem(level) },
            'snout':              { group: prGroup(level),            item: prSnoutItem(level) },
            'babinski':           { group: prGroup(level),            item: `bab-${level}` },
            'spinalgalant':       { group: prGroup(level),            item: `sg-${level}` },
            'atnr':               { group: prGroup(level),            item: `atnr-${level}` },
            'stnr':               { group: prGroup(level),            item: `stnr-${level}` },
            'moro':               { group: prGroup(level),            item: `moro-${level}` },
            'tlr':                { group: prGroup(level),            item: `tlr-${level}` },
            'balance':            { group: 'balance',                 item: `bal-${level}` },
            'curlups':            { group: `core-level-${level}`,     item: `cu-${level}` },
            'lateralsideplank':   { group: `core-level-${level}`,     item: `lsp-${level}` },
            'pronebridge':        { group: `core-level-${level}`,     item: `pb-${level}` },
            'pullups':            { group: `core-level-${level}`,     item: `pu-${level}` },
            'pushups':            { group: `core-level-${level}`,     item: `psh-${level}` },
            'supinebridge':       { group: `core-level-${level}`,     item: `sb-${level}` },
            'oculomotor':         { group: 'oculomotor',              item: `ocul-${level}` },
            'olfactory':          { group: 'olfactory',               item: `olf-${level}` },
            'vestibular':         { group: 'vestibular',              item: `vest-${level}` },
            'interactivemetronome': { group: 'interactive-metronome', item: `int-metro-${level}` },
            'metrotimer':         { group: 'metrotimer',              item: `metro-${level}` },
            'transcranial':       { group: 'transcranial',            item: `trans-${level}` },
            'pons':               { group: 'pons',                    item: `pons-${level}` },
        };

        // Special handling for photobiomodulation - one video per level (levels 1-10)
        if (slug === 'photobiomodulation-left' || slug === 'photobiomodulation-right') {
            const prefix = slug === 'photobiomodulation-left' ? 'left' : 'right';
            const group  = slug === 'photobiomodulation-left' ? 'left-hemisphere' : 'right-hemisphere';
            if (level >= 1 && level <= 10) return `${group}-${prefix}-${level}`;
        }

        const mapping = videoMap[slug];
        return mapping ? `${mapping.group}-${mapping.item}` : null;
    };

    // Returns the video ID for the patient's actual current exercise_level.
    // Used alongside getVideoIdForExercise (which is anchored to level_since_last_eval)
    // to tell Video.js which video to auto-select when the patient has self-progressed.
    //
    // For categories where the group ID embeds the level (primitive reflex, core exercises),
    // level_since_last_eval and exercise_level produce different video IDs — so this function
    // is necessary to get the right auto-selection. For flat-group categories (balance,
    // rhythm/timing, oculomotor, etc.) the group is the same regardless of level, so Video.js
    // already parses the correct level from the item suffix in the URL; those categories
    // don't need this but it's harmless to include them for consistency.
    const getCurrentLevelVideoIdForExercise = (exercise) => {
        const level = exercise.exercise_level; // always the patient's current level
        const slug = exercise.slug;

        // Primitive reflex subgroup IDs use 'pr-levels-' for 1-3 and 'pr-level-' for 4-10
        const prGroup = (lvl) => lvl <= 3 ? `pr-levels-${lvl}` : `pr-level-${lvl}`;

        // Rooting & Snout merge into a single item at level 7+
        const prRootSnoutItem = (lvl) => lvl >= 7 ? `root-snout-${lvl}` : `root-${lvl}`;
        const prSnoutItem = (lvl) => lvl >= 7 ? `root-snout-${lvl}` : `snout-${lvl}`;

        const videoMap = {
            'palmergrasp':          { group: prGroup(level),            item: `pg-${level}` },
            'rooting':              { group: prGroup(level),            item: prRootSnoutItem(level) },
            'snout':                { group: prGroup(level),            item: prSnoutItem(level) },
            'babinski':             { group: prGroup(level),            item: `bab-${level}` },
            'spinalgalant':         { group: prGroup(level),            item: `sg-${level}` },
            'atnr':                 { group: prGroup(level),            item: `atnr-${level}` },
            'stnr':                 { group: prGroup(level),            item: `stnr-${level}` },
            'moro':                 { group: prGroup(level),            item: `moro-${level}` },
            'tlr':                  { group: prGroup(level),            item: `tlr-${level}` },
            'balance':              { group: 'balance',                 item: `bal-${level}` },
            'curlups':              { group: `core-level-${level}`,     item: `cu-${level}` },
            'lateralsideplank':     { group: `core-level-${level}`,     item: `lsp-${level}` },
            'pronebridge':          { group: `core-level-${level}`,     item: `pb-${level}` },
            'pullups':              { group: `core-level-${level}`,     item: `pu-${level}` },
            'pushups':              { group: `core-level-${level}`,     item: `psh-${level}` },
            'supinebridge':         { group: `core-level-${level}`,     item: `sb-${level}` },
            'oculomotor':           { group: 'oculomotor',              item: `ocul-${level}` },
            'olfactory':            { group: 'olfactory',               item: `olf-${level}` },
            'vestibular':           { group: 'vestibular',              item: `vest-${level}` },
            'interactivemetronome': { group: 'interactive-metronome',   item: `int-metro-${level}` },
            'metrotimer':           { group: 'metrotimer',              item: `metro-${level}` },
            'transcranial':         { group: 'transcranial',            item: `trans-${level}` },
            'pons':                 { group: 'pons',                    item: `pons-${level}` },
        };

        // Special handling for photobiomodulation - one video per level (levels 1-10)
        if (slug === 'photobiomodulation-left' || slug === 'photobiomodulation-right') {
            const prefix = slug === 'photobiomodulation-left' ? 'left' : 'right';
            const group  = slug === 'photobiomodulation-left' ? 'left-hemisphere' : 'right-hemisphere';
            if (level >= 1 && level <= 10) return `${group}-${prefix}-${level}`;
        }

        const mapping = videoMap[slug];
        return mapping ? `${mapping.group}-${mapping.item}` : null;
    };

    // Rate limiting functions for exercise increments
    const getIncrementLogsFromStorage = () => {
        try {
            const logs = localStorage.getItem('exerciseIncrementLogs');
            return logs ? JSON.parse(logs) : [];
        } catch (error) {
            console.error('Error reading increment logs:', error);
            return [];
        }
    };

    const saveIncrementLogsToStorage = (logs) => {
        try {
            localStorage.setItem('exerciseIncrementLogs', JSON.stringify(logs));
        } catch (error) {
            console.error('Error saving increment logs:', error);
        }
    };

    const cleanOldLogs = (logs) => {
        const now = Date.now();
        const oneDayInMs = 24 * 60 * 60 * 1000;
        return logs.filter(timestamp => (now - timestamp) < oneDayInMs);
    };

    //const canIncrementExercise = () => {
    //    const logs = getIncrementLogsFromStorage();
    //    const cleanedLogs = cleanOldLogs(logs);
    //    return cleanedLogs.length < MAX_INCREMENTS_PER_DAY;
    //};

    const recordIncrement = () => {
        const logs = getIncrementLogsFromStorage();
        const cleanedLogs = cleanOldLogs(logs);
        cleanedLogs.push(Date.now());
        saveIncrementLogsToStorage(cleanedLogs);
        setExerciseIncrementLogs(cleanedLogs);
    };

    // Load increment logs on component mount
    useEffect(() => {
        const logs = getIncrementLogsFromStorage();
        const cleanedLogs = cleanOldLogs(logs);
        saveIncrementLogsToStorage(cleanedLogs);
        setExerciseIncrementLogs(cleanedLogs);
    }, []);

    // Handle increment for a specific exercise
    // Flattens the exercises map returned by getPatientExercises (root-level exercises
    // plus nested category -> exercise objects) into a plain array of exercise records.
    const flattenAssignedExercises = (data) => {
        const flattened = [];
        if (!data) return flattened;
        Object.values(data).forEach((value) => {
            if (value && typeof value === 'object' && value.exercise_name) {
                flattened.push(value);
            } else if (value && typeof value === 'object') {
                Object.values(value).forEach((exercise) => {
                    if (exercise && exercise.exercise_name) {
                        flattened.push(exercise);
                    }
                });
            }
        });
        return flattened;
    };

    // Given an exercise, decides what its 5/5-completed progress bar means:
    // 'max' (Level 10 ceiling), 'cap' (the 2-level re-evaluation cap), 'levelup'
    // (safe to show the congrats/"Save Progress" popup), or null if it isn't
    // actually sitting at 5/5. Mirrors dashboardRouteHandler.js's own
    // wouldExceedLimit check — single source of truth used by both the live
    // "+" click path and the on-load/on-refresh scan below.
    const getPendingCompletionType = (exercise) => {
        if (Math.abs(exercise?.progress || 0) !== 5) return null;
        const exerciseLevel = parseInt(exercise.exercise_level, 10);
        const levelSinceLastEval = parseInt(
            exercise.level_since_last_eval ?? exercise.exercise_level, 10
        );
        if (exerciseLevel >= 10) return 'max';
        if (exerciseLevel === levelSinceLastEval + 1) return 'cap';
        return 'levelup';
    };

    // True when this exercise has already used up today's logs (+ or -).
    // While true, a 5/5 level-up popup is held back until the next day.
    const isDailyLimitReachedFor = (exercise) => {
        const today = new Date().toISOString().split('T')[0];
        return exercise?.num_logs >= MAX_INCREMENTS_PER_DAY &&
            !!exercise?.date_last_logged?.startsWith(today);
    };

    // Shown whenever a "+" is blocked by the 2-level cap (patient needs a re-evaluation
    // before advancing more than 2 levels past their originally-assigned level).
    const showTwoLevelCapAlert = () => {
        setCustomAlertMessage(
            t("dashboard.ui.twoLevelCapAlert")
        );
        setShowCustomAlert(true);
    };

    // Shown when an exercise's progress bar completes 5/5 at Level 10 — the absolute max.
    const showMaxLevelAlert = () => {
        setCustomAlertMessage(t("dashboard.maxLevelAlert.message"));
        setShowCustomAlert(true);
    };

    // Scans freshly-fetched exercise data for anything sitting at 5/5 that still needs
    // its popup/alert surfaced, and queues it up. Safe to call after every fetch —
    // already-queued, already-showing, and already-alerted (cap/max) exercises are
    // skipped, so this never nags or duplicates. This is what brings back the popup
    // for an exercise that got stuck at 5/5 (stale data, or a failed "Save Progress")
    // the next time the dashboard loads or refreshes.
    const enqueuePendingCompletions = (data) => {
        flattenAssignedExercises(data).forEach((exercise) => {
            const type = getPendingCompletionType(exercise);
            if (!type) return;

            // Daily limit reached — keep the bar at 5/5 today; the level-up popup
            // will surface on the next day's load.
            if (type === 'levelup' && isDailyLimitReachedFor(exercise)) return;

            // 2-level cap alert only shows when the user clicks "+"/"-" on that
            // specific exercise — never automatically on load/refresh.
            if (type === 'cap') return;

            // Already alerted for this cap/max exercise this session — don't re-nag.
            if (type !== 'levelup' && seenCapOrMaxIds.has(exercise.exercise_id)) return;

            // Already the one currently showing.
            if (levelUpExercise?.exercise_id === exercise.exercise_id && showLevelUpModal) return;

            setCompletionQueue((prev) => {
                if (prev.some((item) => item.exercise.exercise_id === exercise.exercise_id)) return prev;
                return [...prev, { exercise, type }];
            });
        });
    };

    // Whenever nothing is currently showing and there's a queued 5/5 exercise, surface
    // its popup/alert. Runs whenever the queue changes, so a stuck exercise gets its
    // prompt back automatically instead of needing another "+" click to trigger it.
    useEffect(() => {
        if (showLevelUpModal || showCustomAlert) return;
        if (completionQueue.length === 0) return;

        const [next, ...rest] = completionQueue;
        setCompletionQueue(rest);

        if (next.type === 'levelup') {
            setLevelUpSaveError(false);
            setLevelUpExercise(next.exercise);
            setShowLevelUpModal(true);
        } else {
            setSeenCapOrMaxIds((prev) => new Set(prev).add(next.exercise.exercise_id));
            if (next.type === 'max') {
                showMaxLevelAlert();
            } else {
                showTwoLevelCapAlert();
            }
        }
    }, [completionQueue, showLevelUpModal, showCustomAlert]);

    // Fires the "plus" API call for an exercise and refreshes assignedExercises. Returns
    // { ok: true, exercise } on success, { ok: false, blocked: true } if the backend
    // rejected it (the 2-level cap alert has already been shown), or
    // { ok: false, blocked: false } on a genuine failure (network/error) so the caller
    // can let the patient retry instead of silently losing their progress.
    const submitPlusForExercise = async (exercise) => {
        // Save scroll position before state update
        const scrollContainer = document.querySelector('[data-scroll-container="assigned-exercises"]');
        const scrollPosition = scrollContainer ? scrollContainer.scrollTop : 0;

        try {
            const response = await recordPatientExercise({
                exerciseId: exercise.exercise_id,
                value: "plus",
                patientId: patient?.patientId
            });

            // If re-evaluation is needed, show alert and stop
            if (response?.data?.counted === false && response?.data?.reason === "Re-evaluation needed!") {
                showTwoLevelCapAlert();
                return { ok: false, blocked: true };
            }

            // Backend refused because today's logs are used up (e.g. the banner was out
            // of date) — mark the exercise as limited and show the daily-limit popup.
            if (response?.data?.counted === false && response?.data?.reason === "Reached daily limit for this exercise.") {
                setExerciseDailyLimitReached(prev => ({ ...prev, [exercise.exercise_id]: true }));
                setShowRateLimitModal(true);
                return { ok: false, blocked: true };
            }

            // Record this increment for rate limiting
            recordIncrement();

            // Refresh assigned exercises to update progress bar
            const { data } = await getPatientExercises(patient?.patientId);
            setAssignedExercises(data || {});
            enqueuePendingCompletions(data || {});

            // Restore scroll position after state update
            setTimeout(() => {
                const container = document.querySelector('[data-scroll-container="assigned-exercises"]');
                if (container) {
                    container.scrollTop = scrollPosition;
                }
            }, 0);

            // Find the refreshed record for this exercise
            const updatedExercise = flattenAssignedExercises(data)
                .find((ex) => ex.exercise_id === exercise.exercise_id) || null;

            // Update the daily limit status based on num_logs and date_last_logged
            if (updatedExercise) {
                const today = new Date().toISOString().split('T')[0];
                setExerciseDailyLimitReached(prev => ({
                    ...prev,
                    [exercise.exercise_id]: updatedExercise.num_logs >= MAX_INCREMENTS_PER_DAY && updatedExercise.date_last_logged?.startsWith(today)
                }));
            }

            return { ok: true, exercise: updatedExercise };
        } catch (error) {
            console.error('Error updating exercise progress:', error);
            return { ok: false, blocked: false };
        }
    };

    // Handle "+" button click
    const handleIncrement = async (exercise) => {
        // Exercise is sitting at 5/5 on the 2-level cap — show the cap popup.
        if (getPendingCompletionType(exercise) === 'cap') {
            showTwoLevelCapAlert();
            return;
        }
        // Daily limit already reached (red banner showing) — show the daily-limit popup instead.
        if (exerciseDailyLimitReached[exercise.exercise_id]) {
            setShowRateLimitModal(true);
            return;
        }
        const currentProgress = Math.abs(exercise.progress || 0);
        // At 5/5 the "+" button is disabled (see isPlusDisabled below) — from there,
        // only "Save Progress" in the level-up popup can fire the real level-up.
        if (currentProgress >= 5) return;

        const result = await submitPlusForExercise(exercise);
        if (!result.ok) return;
        const updatedExercise = result.exercise;
        if (!updatedExercise) return;

        const newProgress = Math.abs(updatedExercise.progress || 0);

        // This was the 5th consecutive "+" — the bar just completed to 5/5.
        // Let the completed 5/5 bar render first, then surface the right popup.
        if (currentProgress === 4 && newProgress === 5) {
            const type = getPendingCompletionType(updatedExercise);

            setTimeout(() => {
                if (type === 'max') {
                    showMaxLevelAlert();
                } else if (type === 'cap') {
                    showTwoLevelCapAlert();
                } else if (type === 'levelup' && !isDailyLimitReachedFor(updatedExercise)) {
                    setLevelUpSaveError(false);
                    setLevelUpExercise(updatedExercise);
                    setShowLevelUpModal(true);
                }
            }, 700);
        }
    };

    // "Save Progress" in the level-up popup — fires the real level-up call on the
    // user's behalf (what used to require a confusing, separately-clicked 6th press).
    const handleSaveLevelUpProgress = async () => {
        if (!levelUpExercise) return;
        setSavingLevelUp(true);
        setLevelUpSaveError(false);
        const result = await submitPlusForExercise(levelUpExercise);
        setSavingLevelUp(false);

        if (!result.ok) {
            if (result.blocked) {
                // Cap alert was already shown by submitPlusForExercise — the exercise's
                // circumstances changed since the popup opened, nothing more to do here.
                setShowLevelUpModal(false);
                setLevelUpExercise(null);
            } else {
                // Genuine failure (network, etc.) — keep the popup open so the patient can
                // retry instead of silently getting stuck at 5/5 with no explanation.
                setLevelUpSaveError(true);
            }
            return;
        }

        setShowLevelUpModal(false);
        setLevelUpExercise(null);
    };

    // Clicking "-" no longer decrements right away — it opens the "Are you sure?"
    // confirm modal instead. The actual reset only happens once "Save" is clicked
    // (see handleConfirmDecrement below).
    const confirmDecrement = (exercise) => {
        // Exercise is sitting at 5/5 on the 2-level cap — show the cap popup.
        if (getPendingCompletionType(exercise) === 'cap') {
            showTwoLevelCapAlert();
            return;
        }
        const currentProgress = Math.abs(exercise.progress || 0);
        // Daily limit already reached (red banner showing) — show the daily-limit popup
        // instead of the demote confirm popup.
        if (exerciseDailyLimitReached[exercise.exercise_id]) {
            setShowRateLimitModal(true);
            return;
        }
        if (currentProgress === 0) return; // Already at 0, nothing to confirm/reset
        setDecrementExercise(exercise);
        setShowDecrementConfirmModal(true);
    };

    // The actual decrement action — fires the "minus" API call and refreshes progress.
    // Only ever called from handleConfirmDecrement below, once the patient has confirmed.
    const handleDecrement = async (exercise) => {
        const currentProgress = Math.abs(exercise.progress || 0);
        if (currentProgress === 0) return; // Can't go below 0
        
        // Save scroll position before state update
        const scrollContainer = document.querySelector('[data-scroll-container="assigned-exercises"]');
        const scrollPosition = scrollContainer ? scrollContainer.scrollTop : 0;
        
        try {
            await recordPatientExercise({
                exerciseId: exercise.exercise_id,
                value: "minus",
                patientId: patient?.patientId
            });
            
            // Refresh assigned exercises to update progress bar
            const { data } = await getPatientExercises(patient?.patientId);
            setAssignedExercises(data || {});
            enqueuePendingCompletions(data || {});

            // "-" also uses up a daily log, so refresh the daily-limit banner here too
            // (previously only the "+" path did this, so the banner didn't appear until
            // the next click).
            const updatedExercise = flattenAssignedExercises(data)
                .find((ex) => ex.exercise_id === exercise.exercise_id) || null;
            if (updatedExercise) {
                setExerciseDailyLimitReached(prev => ({
                    ...prev,
                    [exercise.exercise_id]: isDailyLimitReachedFor(updatedExercise)
                }));
            }

            // Restore scroll position after state update
            setTimeout(() => {
                const container = document.querySelector('[data-scroll-container="assigned-exercises"]');
                if (container) {
                    container.scrollTop = scrollPosition;
                }
            }, 0);
            
        } catch (error) {
            console.error('Error updating exercise progress:', error);
        }
    };

    // "Save" in the decrement-confirm popup — runs the real "-" action the patient just
    // confirmed, then closes the modal.
    const handleConfirmDecrement = async () => {
        if (!decrementExercise) return;
        await handleDecrement(decrementExercise);
        setShowDecrementConfirmModal(false);
        setDecrementExercise(null);
    };

    // Component to display exercises assigned by provider
    const AssignedExercises = () => {
        const scrollContainerRef = useRef(null);
        
        if (user.userType === "provider") {
            return null; // Don't show for providers
        }
        
        // Helper function to translate category slug to display name
        const translateCategorySlug = (categorySlug) => {
            if (!categorySlug) return '';
            
            // Map individual exercise slugs to their parent categories
            const exerciseSlugToCategory = {
                // Core Exercises
                'curlups': 'core-exercises',
                'lateralsideplank': 'core-exercises',
                'pronebridge': 'core-exercises',
                'pullups': 'core-exercises',
                'pushups': 'core-exercises',
                'supinebridge': 'core-exercises',
                
                // Primitive Reflexes
                'palmergrasp': 'primitive-reflexes',
                'rooting': 'primitive-reflexes',
                'snout': 'primitive-reflexes',
                'babinski': 'primitive-reflexes',
                'spinalgalant': 'primitive-reflexes',
                'atnr': 'primitive-reflexes',
                'stnr': 'primitive-reflexes',
                'moro': 'primitive-reflexes',
                'tlr': 'primitive-reflexes',
                
                // Sensory Equipment
                'transcranial': 'sensory-equipment',
                'pons': 'sensory-equipment',
                'photobiomodulation-left': 'sensory-equipment',
                'photobiomodulation-right': 'sensory-equipment',
                'Sensory': 'sensory-equipment',
            };
            
            // Map the category slug to the parent category if needed
            const mappedCategory = exerciseSlugToCategory[categorySlug] || categorySlug;
            
            // Return the translated name
            if (tExerciseCategory(mappedCategory)) {
                return tExerciseCategory(mappedCategory);
            }
            
            // If no translation, format the slug as title case
            return categorySlug
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };
        
        // Helper function to translate exercise names
        const translateExerciseName = (exerciseName) => {
            // Exercise names often come in format "Exercise Name Level X" or just "Exercise Name"
            // We need to extract the base name and translate it, then add back "Level X"
            
            // Check if the name ends with "Level X" pattern
            const levelMatch = exerciseName.match(/^(.+?)\s+Level\s+(\d+)$/i);
            
            if (levelMatch) {
                const baseName = levelMatch[1].trim();
                const levelNumber = levelMatch[2];
                
                // Try to translate the base name
                const translatedBase = tExerciseName(baseName) || baseName;
                return `${translatedBase} ${levelWord} ${levelNumber}`;
            }
            
            // If no "Level X" pattern, try direct translation
            return tExerciseName(exerciseName) || exerciseName;
        };

        // Flatten the nested category structure from backend
        const flattenExercises = () => {
            const flattened = [];
            Object.entries(assignedExercises).forEach(([categoryOrSlug, value]) => {
                // Check if this is a category (nested object) or a direct exercise
                if (value && typeof value === 'object' && value.exercise_name) {
                    // Direct exercise at root level (uncategorized)
                    flattened.push({ slug: categoryOrSlug, category: null, ...value });
                } else if (value && typeof value === 'object') {
                    // Category with nested exercises
                    Object.entries(value).forEach(([slug, exercise]) => {
                        flattened.push({ slug, category: categoryOrSlug, ...exercise });
                    });
                }
            });
            return flattened;
        };

        const exercises = flattenExercises();
        const exerciseCount = exercises.length;

        if (loadingExercises) {
            return (
                <ComponentWrapper>
                    <p className="mb-0" style={{ fontSize: "14px", color: dark ? "#aaa" : "#666" }}>
                        {t("dashboard.yourAssignedExercises.loading")}
                    </p>
                </ComponentWrapper>
            );
        }

        return (
            <ComponentWrapper>
                <div style={{ marginBottom: "15px" }}>
                    <h6 className="mb-2" style={{ 
                        fontWeight: "600",
                        color: dark ? "#fff" : "#333",
                        fontSize: "16px"
                    }}>
                        {t("dashboard.yourAssignedExercises.title")} ({exerciseCount})
                    </h6>
                </div>
                {exerciseCount === 0 ? (
                    <p className="mb-0" style={{ fontSize: "14px", color: dark ? "#aaa" : "#666" }}>
                        {t("dashboard.yourAssignedExercises.noExercises")}
                    </p>
                ) : (
                <div 
                    data-scroll-container="assigned-exercises"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                >
                    {exercises.map((exercise) => (
                        <div 
                            key={exercise.slug} 
                            className="mb-2 p-2"
                            style={{
                                backgroundColor: dark ? "#1f1f1f" : "#fff",
                                borderRadius: "6px",
                                border: `1px solid ${dark ? "#555" : "#e0e0e0"}`
                            }}
                        >
                            <div style={{ 
                                fontWeight: "500",
                                color: dark ? "#fff" : "#333",
                                fontSize: "14px",
                                marginBottom: "6px"
                            }}>
                                {translateExerciseName(exercise.exercise_name)}
                            </div>
                            
                            {/* Daily Limit Alert - Shows when num_logs >= MAX_INCREMENTS_PER_DAY */}
                            {exerciseDailyLimitReached[exercise.exercise_id] && (
                                <div style={{
                                    fontSize: "11px",
                                    color: "#dc3545",
                                    fontWeight: "600",
                                    marginBottom: "6px",
                                    padding: "4px 8px",
                                    backgroundColor: dark ? "rgba(220, 53, 69, 0.1)" : "rgba(220, 53, 69, 0.05)",
                                    borderRadius: "4px",
                                    border: `1px solid ${dark ? "rgba(220, 53, 69, 0.3)" : "rgba(220, 53, 69, 0.2)"}`
                                }}>
                                    {t("dashboard.yourAssignedExercises.dailyLimitReached")}
                                </div>
                            )}
                            
                            <div style={{
                                fontSize: "12px",
                                color: dark ? "#aaa" : "#666",
                                marginBottom: "8px"
                            }}>
                                {exercise.category && (
                                    <span style={{ 
                                        fontWeight: "600",
                                        marginRight: "8px"
                                    }}>
                                        {translateCategorySlug(exercise.category)}
                                    </span>
                                )}
                                {levelWord} {exercise.exercise_level}
                            </div>
                            
                            {/* Progress Bar */}
                            {exercise.slug !== 'transcranial' && <div style={{ marginTop: "8px" }}>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    fontSize: "12px",
                                    marginBottom: "4px"
                                }}>
                                    <span style={{ color: dark ? "#aaa" : "#666" }}>
                                        {t("dashboard.yourAssignedExercises.progress")}
                                    </span>
                                    <span style={{ 
                                        fontWeight: "600",
                                        color: exercise.progress > 0 
                                            ? "#28a745" 
                                            : exercise.progress < 0 
                                            ? "#dc3545" 
                                            : (dark ? "#aaa" : "#666")
                                    }}>
                                        {Math.abs(exercise.progress)}/5
                                    </span>
                                </div>
                                
                                {/* Visual progress bar */}
                                <div style={{
                                    display: "flex",
                                    height: "8px",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    backgroundColor: dark ? "#333" : "#e9ecef"
                                }}>
                                    {[1, 2, 3, 4, 5].map(value => (
                                        <div 
                                            key={value}
                                            style={{
                                                flex: 1,
                                                backgroundColor: Math.abs(exercise.progress) >= value 
                                                    ? (exercise.progress > 0 ? "#28a745" : "#dc3545")
                                                    : "transparent",
                                                marginRight: value < 5 ? "2px" : "0",
                                                transition: "background-color 0.3s ease"
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>}
                            
                            {/* Video Button and Counter Controls */}
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginTop: "12px",
                                gap: "8px"
                            }}>
                                {/* Video Button - only show if video mapping exists */}
                                {(() => {
                                    const videoId = getVideoIdForExercise(exercise);
                                    return videoId ? (
                                        <button
                                            onClick={() => {
                                                // videoId is anchored to level_since_last_eval — it defines the accessible window.
                                                // If the patient has self-progressed above the original assigned level,
                                                // also pass currentLevelId so Video.js auto-selects their actual current level
                                                // instead of the original one, while keeping the window unchanged.
                                                const hasProgressed = exercise.exercise_level > (exercise.level_since_last_eval ?? exercise.exercise_level);
                                                if (hasProgressed) {
                                                    const currentLevelId = getCurrentLevelVideoIdForExercise(exercise);
                                                    history.push(`/video?id=${videoId}&currentLevelId=${currentLevelId}`);
                                                } else {
                                                    history.push(`/video?id=${videoId}`);
                                                }
                                            }}
                                            style={{
                                                padding: "6px 12px",
                                                backgroundColor: "#FDB913",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                cursor: "pointer",
                                                transition: "background-color 0.2s"
                                            }}
                                            onMouseOver={(e) => e.target.style.backgroundColor = "#e5a710"}
                                            onMouseOut={(e) => e.target.style.backgroundColor = "#FDB913"}
                                        >
                                            {t("dashboard.yourAssignedExercises.videoButton")}
                                        </button>
                                    ) : null;
                                })()}
                                
                                {/* Counter Controls */}
                                {exercise.slug !== 'transcranial' && <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px"
                                }}>
                                    {(() => {
                                        // Greyed out once the daily limit is reached, but left clickable
                                        // so the "Daily Limit Reached" popup can show.
                                        const isMinusLimited = !!exerciseDailyLimitReached[exercise.exercise_id];
                                        return (
                                            <button
                                                onClick={() => confirmDecrement(exercise)}
                                                style={{
                                                    width: "28px",
                                                    height: "28px",
                                                    backgroundColor: isMinusLimited
                                                        ? (dark ? "#333" : "#e9ecef")
                                                        : (dark ? "#444" : "#f0f0f0"),
                                                    color: isMinusLimited
                                                        ? (dark ? "#666" : "#aaa")
                                                        : (dark ? "#fff" : "#333"),
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    fontSize: "18px",
                                                    fontWeight: "bold",
                                                    cursor: isMinusLimited ? "not-allowed" : "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    transition: "background-color 0.2s"
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!isMinusLimited) {
                                                        e.target.style.backgroundColor = dark ? "#555" : "#e0e0e0";
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    if (!isMinusLimited) {
                                                        e.target.style.backgroundColor = dark ? "#444" : "#f0f0f0";
                                                    }
                                                }}
                                            >
                                                -
                                            </button>
                                        );
                                    })()}
                                    <span style={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color: dark ? "#fff" : "#333",
                                        minWidth: "20px",
                                        textAlign: "center"
                                    }}>
                                        {Math.abs(exercise.progress || 0)}
                                    </span>
                                    {(() => {
                                        const isPlusLimited = !!exerciseDailyLimitReached[exercise.exercise_id];
                                        // Greyed out at 5/5 or once the daily limit is reached. Only truly
                                        // disabled for 5/5 when the limit ISN'T reached — when it is, the
                                        // button stays clickable so the "Daily Limit Reached" popup can show.
                                        const isPlusDisabled =
                                            isPlusLimited ||
                                            Math.abs(exercise.progress || 0) >= 5 ||
                                            (parseInt(exercise.exercise_level, 10) >= 10 && Math.abs(exercise.progress || 0) >= 5);
                                        return (
                                            <button
                                                onClick={() => handleIncrement(exercise)}
                                                disabled={isPlusDisabled && !isPlusLimited && getPendingCompletionType(exercise) !== 'cap'}
                                                style={{
                                                    width: "28px",
                                                    height: "28px",
                                                    backgroundColor: isPlusDisabled
                                                        ? (dark ? "#333" : "#e9ecef")
                                                        : (dark ? "#444" : "#f0f0f0"),
                                                    color: isPlusDisabled
                                                        ? (dark ? "#666" : "#aaa")
                                                        : (dark ? "#fff" : "#333"),
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    fontSize: "18px",
                                                    fontWeight: "bold",
                                                    cursor: isPlusDisabled ? "not-allowed" : "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    transition: "background-color 0.2s"
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!isPlusDisabled) {
                                                        e.target.style.backgroundColor = dark ? "#555" : "#e0e0e0";
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    if (!isPlusDisabled) {
                                                        e.target.style.backgroundColor = dark ? "#444" : "#f0f0f0";
                                                    }
                                                }}
                                            >
                                                +
                                            </button>
                                        );
                                    })()}
                                </div>}
                            </div>
                        </div>
                    ))}
                </div>
                )}
            </ComponentWrapper>
        );
    };

    // Separate component for Provider Exercise Assignment (purple "Assign Exercises" button)
    // This is ONLY for providers to assign exercises to patients
    const ProviderExerciseAssignment = () => {
        if (user.userType !== "provider") {
            return null;
        }

        // Helper to check if a video is instructional/intro
        const isInstructionalVideo = (itemId, itemName) => {
            const lowerName = itemName.toLowerCase();
            const lowerId = itemId.toLowerCase();
            return (
                lowerName.includes('intro') ||
                lowerName.includes('instruction') ||
                lowerName.includes('overview') ||
                lowerId.includes('intro') ||
                lowerId === 'okn-drum' ||
                lowerId === 'metro-intro' ||
                lowerId === 'laser-intro' ||
                lowerId === 'laser-setup'
            );
        };

        // Helper to get translated group/subgroup name
        const getTranslatedGroupName = (groupId, groupName) => {
            // Check if translation exists for this group ID
            if (tExerciseGroup(groupId)) {
                return tExerciseGroup(groupId);
            }
            // If no translation found, return original name
            return groupName;
        };

        // Helper to translate exercise category names (e.g., "Supine Bridge", "Curl Ups")
        const translateExerciseName = (exerciseName) => {
            // Try to find direct translation in EXERCISE_NAMES
            if (tExerciseName(exerciseName)) {
                return tExerciseName(exerciseName);
            }
            // If no translation found, return original name
            return exerciseName;
        };

        // Helper to extract exercise categories from a group with subgroups
        const getExerciseCategories = (group) => {
            if (!group.subgroups || group.subgroups.length === 0) {
                return null;
            }
            
            // Get all unique exercise types from the first level subgroup
            const firstLevel = group.subgroups[0];
            if (!firstLevel.items) return null;
            
            const categories = firstLevel.items
                .filter(item => !isInstructionalVideo(item.id, item.name))
                .map(item => {
                    // Extract category name (remove "Level X" part)
                    const name = item.name.replace(/\s*Level\s*\d+.*$/i, '').trim();
                    // Extract base ID (remove level number)
                    const baseId = item.id.replace(/-\d+.*$/, '');
                    return { id: baseId, name: name };
                });
            
            // Remove duplicates
            const uniqueCategories = categories.filter((cat, index, self) => 
                index === self.findIndex(c => c.id === cat.id)
            );
            
            return uniqueCategories;
        };

        // Check if a group has exercise categories (like Core)
        const hasExerciseCategories = (group) => {
            const categories = getExerciseCategories(group);
            return categories && categories.length > 0;
        };

        // Check if group is a simple level-based group (like Balance, Oculomotor)
        const isSimpleLevelGroup = (group) => {
            if (!group.items || group.items.length === 0) return false;
            if (group.subgroups && group.subgroups.length > 0) return false;
            
            // Check if items are primarily level-based
            const levelItems = group.items.filter(item => {
                const lowerName = item.name.toLowerCase();
                return lowerName.includes('level') && !isInstructionalVideo(item.id, item.name);
            });
            
            return levelItems.length >= 5; // At least 5 level items
        };

        // Check if group is Rhythm and Timing (special subgroup handling)
        const isRhythmTimingGroup = (group) => {
            return group.id === 'rhythm-timing';
        };

        // Check if group is Sensory Equipment (special nested level input handling)
        const isSensoryGroup = (group) => {
            return group.id === 'sensory-equipment';
        };

        // Check if group should use checkbox assignment (only leftover checkbox groups if any)
        const isCheckboxGroup = (group) => {
            // Removed sensory-equipment, supplements, and sallie-instructions
            return false;
        };

        const handleLevelInput = (e, groupId, categoryId = null) => {
            const value = e.target.value;
            
            // Only allow numbers 1-10
            if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 10)) {
                setLevelInputValue(value);
            }
        };

        const handleLevelSubmit = (e, group, categoryId = null) => {
            if (e.key === 'Enter' && levelInputValue) {
                const level = parseInt(levelInputValue);
                
                if (level >= 1 && level <= 10) {
                    // Create the exercise entry with proper video ID and slug mapping
                    let displayName, videoId, exerciseSlug;
                    
                    // Special handling for Sensory Equipment group
                    if (group.id === 'sensory-equipment' && categoryId) {
                        const subgroupNameMap = {
                            'transcranial': (tExerciseName('Transcranial Stimulation') || 'Transcranial Stimulation'),
                            'pons': (tExerciseName('PONs Stimulator') || 'PONs Stimulator'),
                            'left-hemisphere': (tExerciseName('Photobiomodulation - Left Hemisphere') || 'Photobiomodulation - Left Hemisphere'),
                            'right-hemisphere': (tExerciseName('Photobiomodulation - Right Hemisphere') || 'Photobiomodulation - Right Hemisphere')
                        };
                        
                        displayName = `${subgroupNameMap[categoryId]} - ${levelWord} ${level}`;
                        
                        // Video IDs for Sensory subgroups
                        const videoIdMap = {
                            'transcranial': 'trans',
                            'pons': 'pons',
                            'left-hemisphere': 'left',
                            'right-hemisphere': 'right'
                        };
                        videoId = `${videoIdMap[categoryId]}-${level}`;
                        
                        // Exercise slugs for API
                        const slugMap = {
                            'transcranial': 'transcranial',
                            'pons': 'pons',
                            'left-hemisphere': 'photobiomodulation-left',
                            'right-hemisphere': 'photobiomodulation-right'
                        };
                        exerciseSlug = slugMap[categoryId];
                    }
                    // Special handling for Rhythm-Timing group
                    else if (group.id === 'rhythm-timing' && categoryId) {
                        // categoryId is the subgroup ID (metrotimer or interactive-metronome)
                        const subgroupNameMap = {
                            'metrotimer': (tExerciseName('Metrotimer') || 'Metrotimer'),
                            'interactive-metronome': (tExerciseName('Interactive Metronome') || 'Interactive Metronome')
                        };
                        
                        displayName = `${subgroupNameMap[categoryId]} - ${levelWord} ${level}`;
                        
                        // Video IDs for Rhythm subgroups
                        const videoIdMap = {
                            'metrotimer': 'metro',
                            'interactive-metronome': 'int-metro'
                        };
                        videoId = `${videoIdMap[categoryId]}-${level}`;
                        
                        // Exercise slugs for API
                        const slugMap = {
                            'metrotimer': 'metrotimer',
                            'interactive-metronome': 'interactivemetronome'
                        };
                        exerciseSlug = slugMap[categoryId];
                    }
                    else if (categoryId) {
                        // Category-based (like Core exercises, Primitive Reflexes)
                        const categories = getExerciseCategories(group);
                        const category = categories.find(c => c.id === categoryId);
                        
                        // Map category IDs to their proper English names for translation lookup
                        const categoryIdToNameMap = {
                            // Core Exercises
                            'cu': 'Curl Ups',
                            'lsp': 'Lateral Side Plank',
                            'pb': 'Prone Bridge',
                            'pu': 'Pull Ups',
                            'psh': 'Push Ups',
                            'sb': 'Supine Bridge',
                            // Primitive Reflexes
                            'pg': 'Palmer Grasp',
                            'root': 'Rooting',
                            'snout': 'Snout',
                            'bab': 'Babinski',
                            'sg': 'Spinal Galant',
                            'atnr': 'ATNR',
                            'stnr': 'STNR',
                            'moro': 'Moro',
                            'tlr': 'TLR'
                        };
                        
                        // Use the mapped name if available, otherwise use category.name
                        const baseExerciseName = categoryIdToNameMap[categoryId] || category.name;
                        
                        // Translate the category name if available in EXERCISE_NAMES
                        const translatedName = tExerciseName(baseExerciseName) || category.name;
                        displayName = `${translatedName} - ${levelWord} ${level}`;
                        
                        // The videoId is just the item ID from VideoConfig (e.g., "cu-5" for Curl Ups Level 5)
                        videoId = `${categoryId}-${level}`;
                        
                        // Map category ID to exercise slug for API
                        const categorySlugMap = {
                            'cu': 'curlups',
                            'lsp': 'lateralsideplank',
                            'pb': 'pronebridge',
                            'pu': 'pullups',
                            'psh': 'pushups',
                            'sb': 'supinebridge',
                            'pg': 'palmergrasp',
                            'root': 'rooting',
                            'snout': 'snout',
                            'bab': 'babinski',
                            'sg': 'spinalgalant',
                            'atnr': 'atnr',
                            'stnr': 'stnr',
                            'moro': 'moro',
                            'tlr': 'tlr'
                        };
                        exerciseSlug = categorySlugMap[categoryId] || categoryId;
                    } else {
                        // Simple level-based (like Balance, Oculomotor, Vestibular, Olfactory)
                        // Map group ID to the base exercise name for translation lookup
                        const groupNameMap = {
                            'balance': 'Balance',
                            'oculomotor': 'Oculomotor',
                            'vestibular': 'Vestibular',
                            'olfactory': 'Olfactory'
                        };
                        
                        const baseExerciseName = groupNameMap[group.id] || group.name;
                        const translatedGroupName = tExerciseName(baseExerciseName) || group.name;
                        displayName = `${translatedGroupName} - ${levelWord} ${level}`;
                        
                        // Map group ID to video item ID prefix
                        const groupVideoIdMap = {
                            'balance': 'bal',
                            'oculomotor': 'ocul',
                            'vestibular': 'vest',
                            'olfactory': 'olf',
                            'metrotimer': 'metro',
                            'interactive-metronome': 'int-metro'
                        };
                        
                        // Map group ID to exercise slug for API
                        const groupSlugMap = {
                            'balance': 'balance',
                            'oculomotor': 'oculomotor',
                            'vestibular': 'vestibular',
                            'olfactory': 'olfactory',
                            'metrotimer': 'metrotimer',
                            'interactive-metronome': 'interactivemetronome'
                        };
                        
                        const videoIdPrefix = groupVideoIdMap[group.id] || group.id;
                        videoId = `${videoIdPrefix}-${level}`;
                        exerciseSlug = groupSlugMap[group.id] || group.id;
                    }
                    
                    // Check if this exercise is already selected
                    const exists = selectedExercisesForAssignment.some(
                        ex => ex.groupId === group.id && ex.categoryId === categoryId
                    );
                    
                    if (!exists) {
                        setSelectedExercisesForAssignment(prev => [
                            ...prev,
                            {
                                groupId: group.id,
                                categoryId: categoryId,
                                level: level,
                                displayName: displayName,
                                videoId: videoId,
                                slug: exerciseSlug
                            }
                        ]);
                    } else {
                        // Update existing
                        setSelectedExercisesForAssignment(prev => 
                            prev.map(ex => 
                                (ex.groupId === group.id && ex.categoryId === categoryId)
                                    ? { ...ex, level: level, displayName: displayName, videoId: videoId }
                                    : ex
                            )
                        );
                    }
                    
                    // Reset input
                    setLevelInputValue('');
                    setActiveGroupInput(null);
                }
            } else if (e.key === 'Escape') {
                setLevelInputValue('');
                setActiveGroupInput(null);
            }
        };

        const confirmAssignment = async () => {
            const exercises = {};
            
            // Add level-based exercises from the new UI
            selectedExercisesForAssignment.forEach(exercise => {
                exercises[exercise.slug] = exercise.level;
            });
            
            // Add checkbox-based selections (for Sensory, Supplements, Sallie groups)
            const selectedVideoIds = Object.keys(selectedVideos).filter(id => selectedVideos[id]);
            
            // Map checkbox selections to exercise slugs and levels
            selectedVideoIds.forEach(videoId => {
                // Extract the actual video item ID (remove group prefix)
                // Video IDs are in format: "groupId-itemId" e.g., "transcranial-trans-1"
                const parts = videoId.split('-');
                
                // Try to extract level from the ID
                let slug = null;
                let level = null;
                
                // Transcranial exercises: trans-1, trans-2, etc.
                if (videoId.includes('trans-') && /trans-\d+$/.test(videoId)) {
                    const match = videoId.match(/trans-(\d+)$/);
                    if (match) {
                        slug = 'transcranial';
                        level = parseInt(match[1]);
                    }
                }
                // PONs exercises: pons-1, pons-2, etc.
                else if (videoId.includes('pons-') && /pons-\d+$/.test(videoId)) {
                    const match = videoId.match(/pons-(\d+)$/);
                    if (match) {
                        slug = 'pons';
                        level = parseInt(match[1]);
                    }
                }
                // Photobiomodulation - Left hemisphere: left-1, left-2, ... left-10
                else if (videoId.includes('left-')) {
                    const match = videoId.match(/left-(\d+)$/);
                    if (match) {
                        slug = 'photobiomodulation-left';
                        level = parseInt(match[1]);
                    }
                }
                // Photobiomodulation - Right hemisphere: right-1, right-2, ... right-10
                else if (videoId.includes('right-')) {
                    const match = videoId.match(/right-(\d+)$/);
                    if (match) {
                        slug = 'photobiomodulation-right';
                        level = parseInt(match[1]);
                    }
                }
                
                // If we found a valid exercise mapping, add it
                if (slug && level) {
                    // Only update if this exercise hasn't been added yet, or use the highest level
                    if (!exercises[slug] || level > exercises[slug]) {
                        exercises[slug] = level;
                    }
                } else {
                    // This is an instructional video, log it but don't add to exercises
                    console.log('Checkbox video selected (informational, not an exercise):', videoId);
                }
            });
            
            if (Object.keys(exercises).length === 0) {
                showAlert(t("dashboard.confirmAssignmentModal.noExercisesSelected"));
                return;
            }

            const assignmentData = {
                patientId: patient?.patientId,
                exercises: exercises
            };
            
            console.log('Sending assignment data:', JSON.stringify(assignmentData, null, 2));

            try {
                const response = await sendAssignments(assignmentData);
                console.log('Assignment successful:', response.data);
                
                // Refresh the provider's view of assigned exercises immediately
                const { data } = await getPatientExercises(patient.patientId);
                setProviderViewAssignedExercises(data || {});
                
                setShowConfirmModal(false);
                setSelectedExercisesForAssignment([]);
                setSelectedVideos({});
                
                showAlert(`${t("dashboard.confirmAssignmentModal.successMessage")} ${Object.keys(exercises).length} ${t("dashboard.confirmAssignmentModal.exercisesToPatient")}`);
            } catch (error) {
                console.error('Error assigning exercises:', error);
                console.error('Response data:', error.response?.data);
                
                let errorMessage = t("dashboard.confirmAssignmentModal.failedToAssign") + ' ';
                if (error.response?.data?.message) {
                    errorMessage += error.response.data.message;
                } else {
                    errorMessage += t("dashboard.confirmAssignmentModal.pleaseTryAgain");
                }
                
                showAlert(errorMessage);
            }
        };

        const toggleAssignmentSection = (sectionId, isTopLevel = false) => {
            setExpandedAssignmentSections(prev => {
                // If clicking on an already open section, close it
                if (prev[sectionId]) {
                    return {
                        ...prev,
                        [sectionId]: false
                    };
                }
                
                // If it's a top-level group, close all OTHER top-level groups
                if (isTopLevel) {
                    // Get list of top-level group IDs
                    const topLevelGroupIds = filteredVideoGroups.map(g => g.id);
                    
                    // Create new state keeping only nested subgroups open
                    const newState = {};
                    Object.keys(prev).forEach(key => {
                        // Keep nested subgroups (not in top-level list) that are open
                        if (!topLevelGroupIds.includes(key) && prev[key]) {
                            newState[key] = true;
                        }
                    });
                    
                    // Open the clicked top-level section
                    newState[sectionId] = true;
                    
                    // Also clear any active simple level group inputs
                    setActiveGroupInput(null);
                    setLevelInputValue('');
                    
                    return newState;
                }
                
                // If it's a nested subgroup, just toggle it without affecting top-level groups
                return {
                    ...prev,
                    [sectionId]: true
                };
            });
        };

        const renderSimpleLevelGroup = (group) => {
            const isActive = activeGroupInput?.groupId === group.id && !activeGroupInput?.categoryId;
            
            return (
                <div key={group.id} className="video-group">
                    <div 
                        className="checklist-header"
                        onClick={() => {
                            setActiveGroupInput({ groupId: group.id, categoryId: null });
                            setLevelInputValue('');
                            // Close all accordion sections when a simple level group is clicked
                            setExpandedAssignmentSections({});
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <span>{getTranslatedGroupName(group.id, group.name)}</span>
                    </div>
                    
                    {isActive && (
                        <div style={{ padding: '10px 20px' }}>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '5px',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}>
                                {t("dashboard.levelInput.enterLevel")}
                            </label>
                            <input
                                type="text"
                                value={levelInputValue}
                                onChange={(e) => handleLevelInput(e, group.id)}
                                onKeyDown={(e) => handleLevelSubmit(e, group)}
                                placeholder={t("dashboard.levelInput.placeholder")}
                                autoFocus
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #6f42c1',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    outline: 'none'
                                }}
                            />
                            <small style={{ 
                                display: 'block', 
                                marginTop: '5px',
                                color: '#666',
                                fontSize: '12px'
                            }}>
                                {t("dashboard.levelInput.instructions")}
                            </small>
                        </div>
                    )}
                </div>
            );
        };

        const renderCategoryBasedGroup = (group) => {
            const categories = getExerciseCategories(group);
            
            return (
                <div key={group.id} className="video-group">
                    <div 
                        className="checklist-header"
                        onClick={() => toggleAssignmentSection(group.id, true)}
                        aria-controls={`${group.id}-collapse`}
                        aria-expanded={expandedAssignmentSections[group.id]}
                        style={{ cursor: 'pointer' }}
                    >
                        <span className={`arrow ${expandedAssignmentSections[group.id] ? 'expanded' : ''}`}>▶</span>
                        <span>{getTranslatedGroupName(group.id, group.name)}</span>
                    </div>
                    
                    <Collapse in={expandedAssignmentSections[group.id]}>
                        <div id={`${group.id}-collapse`} className="checklist-content">
                            {categories.map(category => {
                                const isActive = activeGroupInput?.groupId === group.id && 
                                               activeGroupInput?.categoryId === category.id;
                                
                                return (
                                    <div key={category.id}>
                                        <div 
                                            className="checklist-item"
                                            onClick={() => {
                                                setActiveGroupInput({ groupId: group.id, categoryId: category.id });
                                                setLevelInputValue('');
                                            }}
                                            style={{ cursor: 'pointer', padding: '8px 10px' }}
                                        >
                                            <span>{translateExerciseName(category.name)}</span>
                                        </div>
                                        
                                        {isActive && (
                                            <div style={{ padding: '10px 30px' }}>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '5px',
                                                    fontSize: '14px',
                                                    fontWeight: '500'
                                                }}>
                                                    {t("dashboard.levelInput.enterLevel")}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={levelInputValue}
                                                    onChange={(e) => handleLevelInput(e, group.id, category.id)}
                                                    onKeyDown={(e) => handleLevelSubmit(e, group, category.id)}
                                                    placeholder={t("dashboard.levelInput.placeholder")}
                                                    autoFocus
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        border: '2px solid #6f42c1',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                        outline: 'none'
                                                    }}
                                                />
                                                <small style={{ 
                                                    display: 'block', 
                                                    marginTop: '5px',
                                                    color: '#666',
                                                    fontSize: '12px'
                                                }}>
                                                    {t("dashboard.levelInput.instructions")}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Collapse>
                </div>
            );
        };

        // Render Rhythm and Timing group with subgroup categories
        const renderRhythmTimingGroup = (group) => {
            return (
                <div key={group.id} className="video-group">
                    <div 
                        className="checklist-header"
                        onClick={() => toggleAssignmentSection(group.id, true)}
                        aria-controls={`${group.id}-collapse`}
                        aria-expanded={expandedAssignmentSections[group.id]}
                        style={{ cursor: 'pointer' }}
                    >
                        <span className={`arrow ${expandedAssignmentSections[group.id] ? 'expanded' : ''}`}>▶</span>
                        <span>{getTranslatedGroupName(group.id, group.name)}</span>
                    </div>
                    
                    <Collapse in={expandedAssignmentSections[group.id]}>
                        <div id={`${group.id}-collapse`} className="checklist-content">
                            {group.subgroups && group.subgroups.map(subgroup => {
                                const isActive = activeGroupInput?.groupId === group.id && 
                                               activeGroupInput?.categoryId === subgroup.id;
                                
                                return (
                                    <div key={subgroup.id}>
                                        <div 
                                            className="checklist-item"
                                            onClick={() => {
                                                setActiveGroupInput({ groupId: group.id, categoryId: subgroup.id });
                                                setLevelInputValue('');
                                            }}
                                            style={{ cursor: 'pointer', padding: '8px 10px' }}
                                        >
                                            <span>{getTranslatedGroupName(subgroup.id, subgroup.name)}</span>
                                        </div>
                                        
                                        {isActive && (
                                            <div style={{ padding: '10px 30px' }}>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '5px',
                                                    fontSize: '14px',
                                                    fontWeight: '500'
                                                }}>
                                                    {t("dashboard.levelInput.enterLevel")}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={levelInputValue}
                                                    onChange={(e) => handleLevelInput(e, group.id, subgroup.id)}
                                                    onKeyDown={(e) => handleLevelSubmit(e, group, subgroup.id)}
                                                    placeholder={t("dashboard.levelInput.placeholder")}
                                                    autoFocus
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        border: '2px solid #6f42c1',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                        outline: 'none'
                                                    }}
                                                />
                                                <small style={{ 
                                                    display: 'block', 
                                                    marginTop: '5px',
                                                    color: '#666',
                                                    fontSize: '12px'
                                                }}>
                                                    {t("dashboard.levelInput.instructions")}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Collapse>
                </div>
            );
        };

        // Render Sensory Equipment group with nested level-based subgroups
        const renderSensoryGroup = (group) => {
            return (
                <div key={group.id} className="video-group">
                    <div 
                        className="checklist-header"
                        onClick={() => toggleAssignmentSection(group.id, true)}
                        aria-controls={`${group.id}-collapse`}
                        aria-expanded={expandedAssignmentSections[group.id]}
                        style={{ cursor: 'pointer' }}
                    >
                        <span className={`arrow ${expandedAssignmentSections[group.id] ? 'expanded' : ''}`}>▶</span>
                        <span>{getTranslatedGroupName(group.id, group.name)}</span>
                    </div>
                    
                    <Collapse in={expandedAssignmentSections[group.id]}>
                        <div id={`${group.id}-collapse`} className="checklist-content">
                            {/* Main level items (if any) - render without checkboxes */}
                            {group.items && group.items.map(item => renderItemWithoutCheckbox(item, group.id))}
                            
                            {/* Process subgroups */}
                            {group.subgroups && group.subgroups.map(subgroup => {
                                // Check if this subgroup has level-based items
                                const hasLevels = subgroup.items && subgroup.items.some(item => 
                                    /\d+$/.test(item.id) && item.name.toLowerCase().includes('level')
                                );
                                
                                // Photobiomodulation has nested subgroups (Left/Right hemisphere)
                                if (subgroup.id === 'photobiomodulation' && subgroup.subgroups) {
                                    return (
                                        <div key={subgroup.id} className="nested-section">
                                            <div 
                                                className="nested-header"
                                                onClick={() => toggleAssignmentSection(subgroup.id)}
                                                aria-controls={`${subgroup.id}-collapse`}
                                                aria-expanded={expandedAssignmentSections[subgroup.id]}
                                            >
                                                <span className={`arrow ${expandedAssignmentSections[subgroup.id] ? 'expanded' : ''}`}>▶</span>
                                                <span>{getTranslatedGroupName(subgroup.id, subgroup.name)}</span>
                                            </div>
                                            
                                            <Collapse in={expandedAssignmentSections[subgroup.id]}>
                                                <div id={`${subgroup.id}-collapse`} className="nested-content">
                                                    {/* Left and Right Hemisphere subgroups */}
                                                    {subgroup.subgroups.map(nestedSubgroup => {
                                                        const isActive = activeGroupInput?.groupId === group.id && 
                                                                       activeGroupInput?.categoryId === nestedSubgroup.id;
                                                        
                                                        return (
                                                            <div key={nestedSubgroup.id}>
                                                                <div 
                                                                    className="checklist-item"
                                                                    onClick={() => {
                                                                        setActiveGroupInput({ groupId: group.id, categoryId: nestedSubgroup.id });
                                                                        setLevelInputValue('');
                                                                    }}
                                                                    style={{ cursor: 'pointer', padding: '8px 10px', marginLeft: '10px' }}
                                                                >
                                                                    <span>{getTranslatedGroupName(nestedSubgroup.id, nestedSubgroup.name)}</span>
                                                                </div>
                                                                
                                                                {isActive && (
                                                                    <div style={{ padding: '10px 40px' }}>
                                                                        <label style={{ 
                                                                            display: 'block', 
                                                                            marginBottom: '5px',
                                                                            fontSize: '14px',
                                                                            fontWeight: '500'
                                                                        }}>
                                                                            {t("dashboard.levelInput.enterLevelShort")}
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            value={levelInputValue}
                                                                            onChange={(e) => handleLevelInput(e, group.id, nestedSubgroup.id)}
                                                                            onKeyDown={(e) => handleLevelSubmit(e, group, nestedSubgroup.id)}
                                                                            placeholder={t("dashboard.levelInput.placeholderShort")}
                                                                            autoFocus
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '8px 12px',
                                                                                border: '2px solid #6f42c1',
                                                                                borderRadius: '4px',
                                                                                fontSize: '14px',
                                                                                outline: 'none'
                                                                            }}
                                                                        />
                                                                        <small style={{ 
                                                                            display: 'block', 
                                                                            marginTop: '5px',
                                                                            color: '#666',
                                                                            fontSize: '12px'
                                                                        }}>
                                                                            {t("dashboard.levelInput.instructions")}
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </Collapse>
                                        </div>
                                    );
                                }
                                // Transcranial and PONs: direct level input
                                else if (hasLevels) {
                                    const isActive = activeGroupInput?.groupId === group.id && 
                                                   activeGroupInput?.categoryId === subgroup.id;
                                    
                                    return (
                                        <div key={subgroup.id}>
                                            <div 
                                                className="checklist-item"
                                                onClick={() => {
                                                    setActiveGroupInput({ groupId: group.id, categoryId: subgroup.id });
                                                    setLevelInputValue('');
                                                }}
                                                style={{ cursor: 'pointer', padding: '8px 10px' }}
                                            >
                                                <span>{getTranslatedGroupName(subgroup.id, subgroup.name)}</span>
                                            </div>
                                            
                                            {isActive && (
                                                <div style={{ padding: '10px 30px' }}>
                                                    <label style={{ 
                                                        display: 'block', 
                                                        marginBottom: '5px',
                                                        fontSize: '14px',
                                                        fontWeight: '500'
                                                    }}>
                                                        {t("dashboard.levelInput.enterLevel")}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={levelInputValue}
                                                        onChange={(e) => handleLevelInput(e, group.id, subgroup.id)}
                                                        onKeyDown={(e) => handleLevelSubmit(e, group, subgroup.id)}
                                                        placeholder={t("dashboard.levelInput.placeholder")}
                                                        autoFocus
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 12px',
                                                            border: '2px solid #6f42c1',
                                                            borderRadius: '4px',
                                                            fontSize: '14px',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                    <small style={{ 
                                                        display: 'block', 
                                                        marginTop: '5px',
                                                        color: '#666',
                                                        fontSize: '12px'
                                                    }}>
                                                        {t("dashboard.levelInput.instructions")}
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                // Eyelights and other non-level subgroups: render without checkboxes
                                else {
                                    return (
                                        <div key={subgroup.id} className="nested-section">
                                            <div 
                                                className="nested-header"
                                                onClick={() => toggleAssignmentSection(subgroup.id)}
                                                aria-controls={`${subgroup.id}-collapse`}
                                                aria-expanded={expandedAssignmentSections[subgroup.id]}
                                            >
                                                <span className={`arrow ${expandedAssignmentSections[subgroup.id] ? 'expanded' : ''}`}>▶</span>
                                                <span>{getTranslatedGroupName(subgroup.id, subgroup.name)}</span>
                                            </div>
                                            
                                            <Collapse in={expandedAssignmentSections[subgroup.id]}>
                                                <div id={`${subgroup.id}-collapse`} className="nested-content">
                                                    {subgroup.items && subgroup.items.map(item => renderItemWithoutCheckbox(item, subgroup.id))}
                                                </div>
                                            </Collapse>
                                        </div>
                                    );
                                }
                            })}
                        </div>
                    </Collapse>
                </div>
            );
        };

        // Helper function for checkbox-based groups
        const toggleVideoSelection = (videoId) => {
            setSelectedVideos(prev => ({
                ...prev,
                [videoId]: !prev[videoId]
            }));
        };

        const renderAssignmentVideoItem = (item, groupId) => {
            const videoId = `${groupId}-${item.id}`;
            const isSelected = selectedVideos[videoId];
            
            return (
                <div 
                    key={videoId} 
                    className="checklist-item"
                    style={{ cursor: 'pointer' }}
                >
                    <input
                        type="checkbox"
                        id={videoId}
                        checked={isSelected || false}
                        onChange={() => toggleVideoSelection(videoId)}
                        className="video-checkbox"
                    />
                    <label 
                        htmlFor={videoId} 
                        className="video-label"
                        style={{ cursor: 'pointer' }}
                    >
                        {item.name}
                    </label>
                </div>
            );
        };

        // Render item without checkbox (for sensory equipment items)
        // These are instructional videos - display only, not assignable
        const renderItemWithoutCheckbox = (item, groupId) => {
            const videoId = `${groupId}-${item.id}`;
            
            return (
                <div 
                    key={videoId} 
                    className="checklist-item"
                    style={{ 
                        cursor: 'default', 
                        padding: '8px 10px',
                        backgroundColor: 'transparent',
                        fontWeight: 'normal'
                    }}
                >
                    <span>{item.name}</span>
                </div>
            );
        };

        // Render checkbox-based groups (Sensory, Supplements, Sallie)
        const renderCheckboxGroup = (group) => {
            return (
                <div key={group.id} className="video-group">
                    <div 
                        className="checklist-header"
                        onClick={() => toggleAssignmentSection(group.id, true)}
                        aria-controls={`${group.id}-collapse`}
                        aria-expanded={expandedAssignmentSections[group.id]}
                        style={{ cursor: 'pointer' }}
                    >
                        <span className={`arrow ${expandedAssignmentSections[group.id] ? 'expanded' : ''}`}>▶</span>
                        <span>{getTranslatedGroupName(group.id, group.name)}</span>
                    </div>
                    
                    <Collapse in={expandedAssignmentSections[group.id]}>
                        <div id={`${group.id}-collapse`} className="checklist-content">
                            {group.items && group.items.map(item => renderAssignmentVideoItem(item, group.id))}
                            
                            {group.subgroups && group.subgroups.map((subgroup) => (
                                <div key={subgroup.id} className="nested-section">
                                    <div 
                                        className="nested-header"
                                        onClick={() => toggleAssignmentSection(subgroup.id)}
                                        aria-controls={`${subgroup.id}-collapse`}
                                        aria-expanded={expandedAssignmentSections[subgroup.id]}
                                    >
                                        <span className={`arrow ${expandedAssignmentSections[subgroup.id] ? 'expanded' : ''}`}>▶</span>
                                        <span>{getTranslatedGroupName(subgroup.id, subgroup.name)}</span>
                                    </div>
                                    
                                    <Collapse in={expandedAssignmentSections[subgroup.id]}>
                                        <div id={`${subgroup.id}-collapse`} className="nested-content">
                                            {subgroup.items && subgroup.items.map(item => renderAssignmentVideoItem(item, subgroup.id))}
                                            
                                            {subgroup.subgroups && subgroup.subgroups.map((nestedSubgroup) => (
                                                <div key={nestedSubgroup.id} className="nested-section">
                                                    <div 
                                                        className="nested-header"
                                                        onClick={() => toggleAssignmentSection(nestedSubgroup.id)}
                                                        aria-controls={`${nestedSubgroup.id}-collapse`}
                                                        aria-expanded={expandedAssignmentSections[nestedSubgroup.id]}
                                                    >
                                                        <span className={`arrow ${expandedAssignmentSections[nestedSubgroup.id] ? 'expanded' : ''}`}>▶</span>
                                                        <span>{getTranslatedGroupName(nestedSubgroup.id, nestedSubgroup.name)}</span>
                                                    </div>
                                                    
                                                    <Collapse in={expandedAssignmentSections[nestedSubgroup.id]}>
                                                        <div id={`${nestedSubgroup.id}-collapse`} className="nested-content">
                                                            {nestedSubgroup.items.map(item => renderAssignmentVideoItem(item, nestedSubgroup.id))}
                                                        </div>
                                                    </Collapse>
                                                </div>
                                            ))}
                                        </div>
                                    </Collapse>
                                </div>
                            ))}
                            
                            {group.additionalItems && group.additionalItems.map(item => renderAssignmentVideoItem(item, group.id))}
                        </div>
                    </Collapse>
                </div>
            );
        };

        return (
            <>
                <div className="mb-4 mt-3">
                    <button
                        style={{
                            width: "100%",
                            padding: "12px 20px",
                            backgroundColor: "#6f42c1",  
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "16px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
                        }}
                        onClick={() => setShowAssignmentDropdown(!showAssignmentDropdown)}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = "#5a32a3";
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.15)";
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = "#6f42c1";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                        }}
                    >
                        {t("dashboard.assignExercisesButton")}
                    </button>

                    {showAssignmentDropdown && (
                        <div style={{ marginTop: "15px" }}>
                            <div className="video-sidebar-panel">
                                <div className="sidebar-header">
                                    <h5>{t("dashboard.selectExercisesToAssign")}</h5>
                                </div>
                                
                                <div className="video-checklist-container">
                                    {filteredVideoGroups.map((group) => {
                                        if (isCheckboxGroup(group)) {
                                            return renderCheckboxGroup(group);
                                        } else if (isSensoryGroup(group)) {
                                            return renderSensoryGroup(group);
                                        } else if (isRhythmTimingGroup(group)) {
                                            return renderRhythmTimingGroup(group);
                                        } else if (isSimpleLevelGroup(group)) {
                                            return renderSimpleLevelGroup(group);
                                        } else if (hasExerciseCategories(group)) {
                                            return renderCategoryBasedGroup(group);
                                        } else {
                                            // Skip groups that don't fit any pattern
                                            return null;
                                        }
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Confirmation Modal */}
                <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{t("dashboard.confirmAssignmentModal.title")}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>{t("dashboard.confirmAssignmentModal.confirmQuestion")}</p>
                        <ul>
                            {selectedExercisesForAssignment.map((exercise, index) => (
                                <li key={`modal-level-${index}`}>{exercise.displayName}</li>
                            ))}
                            {Object.keys(selectedVideos)
                                .filter(id => selectedVideos[id])
                                .map((videoId, index) => (
                                    <li key={`modal-checkbox-${index}`}>{videoId}</li>
                                ))}
                        </ul>
                        <p className="text-muted mt-3">
                            {t("dashboard.confirmAssignmentModal.total")}: <strong>{selectedExercisesForAssignment.length + Object.keys(selectedVideos).filter(id => selectedVideos[id]).length}</strong> {t("dashboard.confirmAssignmentModal.items")}
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button 
                            variant="danger" 
                            onClick={confirmAssignment}
                            style={{
                                borderRadius: "50px",
                                paddingLeft: "30px",
                                paddingRight: "30px"
                            }}
                        >
                            {t("dashboard.confirmAssignmentModal.saveAndCloseButton")}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    };
    // Dark mode toggle - sits at the far left of the button row on every dashboard
    const ThemeChangerButton = () => (
        <Col md={3}>
            <ThemeChanger align="center" />
        </Col>
    );

    const VideoTutorialsButton = () => {
        // Build a comma-separated list of all assigned exercise video IDs so the
        // Video page can apply the same gray-out logic as the small per-exercise button.
        const handleVideoTutorialsClick = () => {
            const flattenExercises = () => {
                const flattened = [];
                Object.entries(assignedExercises).forEach(([categoryOrSlug, value]) => {
                    if (value && typeof value === 'object' && value.exercise_name) {
                        flattened.push({ slug: categoryOrSlug, ...value });
                    } else if (value && typeof value === 'object') {
                        Object.entries(value).forEach(([slug, exercise]) => {
                            flattened.push({ slug, ...exercise });
                        });
                    }
                });
                return flattened;
            };

            const exercises = flattenExercises();
            if (exercises.length > 0) {
                const ids = exercises
                    .map(ex => getVideoIdForExercise(ex))
                    .filter(Boolean);
                if (ids.length > 0) {
                    history.push(`/video?ids=${ids.join(',')}`);
                    return;
                }
            }
            // No assigned exercises (e.g. provider) — open unrestricted
            history.push('/video');
        };

        return (
            <Col md={3}>
                <div className="d-flex justify-content-center">
                    <h6
                        className="pt-2 pb-2 pl-3 pr-3"
                        style={{
                            width: "75%",
                            height: "100%",
                            borderRadius: "20px",
                            backgroundColor: "#FDB913",
                            color: "white",
                            textAlign: "center",
                            overflowWrap: "break-word",
                            cursor: "pointer",
                        }}
                        onClick={handleVideoTutorialsClick}
                    >
                        {t("dashboard.videoTutorialsButton")}
                    </h6>
                </div>
            </Col>
        );
    };

    const VerifyEmailMessage =
        user.isVerified === false &&
        user.userType !== "provider" &&
        showPopUp ? (
            <Alert
                variant="warning"
                onClose={() => {
                    setShowPopUp(false);
                }}
                dismissible
            >
                <Alert.Heading>{t("dashboard.verifyEmail.header")}</Alert.Heading>
                <p>{t("dashboard.verifyEmail.body.0")}</p>
                <p>
                    {`${t("dashboard.verifyEmail.body.1")} ${user.email}${t("dashboard.verifyEmail.body.2")}`}
                </p>
            </Alert>
        ) : null;

    const getPossibleMessages = () => ({
        sallieText: {
            Stroke: t("dashboard.sallieDashboard.patient"),
            tbiPatient: t("dashboard.sallieDashboard.patient"),
            caregiver: t("dashboard.sallieDashboard.nonPatient"),
            provider: t("dashboard.sallieDashboard.nonPatient"),
        },
    });

    const getMessage = customizeMessages({ user, getPossibleMessages });

    // Rate Limit Modal Component
    const RateLimitModal = () => (
        <Modal 
            show={showRateLimitModal} 
            onHide={() => setShowRateLimitModal(false)}
            centered
        >
            <Modal.Header closeButton style={{ 
                backgroundColor: dark ? "#2d2d2d" : "#fff",
                borderBottom: `1px solid ${dark ? "#444" : "#dee2e6"}`
            }}>
                <Modal.Title style={{ 
                    color: dark ? "#fff" : "#333",
                    fontSize: "18px",
                    fontWeight: "600"
                }}>
                    {t("dashboard.ui.dailyLimit.title")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ 
                backgroundColor: dark ? "#2d2d2d" : "#fff",
                color: dark ? "#ddd" : "#333"
            }}>
                <p style={{ marginBottom: "15px", fontSize: "15px" }}>
                    {t("dashboard.ui.dailyLimit.reached")}
                </p>
                <p style={{ marginBottom: "0", fontSize: "15px" }}>
                    {t("dashboard.ui.dailyLimit.canLogPrefix")} <strong>{t("dashboard.ui.dailyLimit.times", { count: MAX_INCREMENTS_PER_DAY })}</strong> {t("dashboard.ui.dailyLimit.canLogSuffix")}
                </p>
            </Modal.Body>
            <Modal.Footer style={{ 
                backgroundColor: dark ? "#2d2d2d" : "#fff",
                borderTop: `1px solid ${dark ? "#444" : "#dee2e6"}`
            }}>
                <Button 
                    variant="primary" 
                    onClick={() => setShowRateLimitModal(false)}
                    style={{
                        backgroundColor: "#28a745",
                        border: "none",
                        padding: "8px 20px",
                        fontWeight: "600"
                    }}
                >
                    {t("dashboard.ui.okay")}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    // Completion Modal Component - styled to match Provider Assignment Modal
    const CompletionModal = () => (
        <Modal show={showCompletionModal} onHide={() => setShowCompletionModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t("dashboard.ui.completion.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{t("dashboard.ui.completion.congrats")}</p>
                <p>{t("dashboard.ui.completion.schedule")} <strong>mm@gmail.com</strong></p>
            </Modal.Body>
            <Modal.Footer>
                <Button 
                    variant="success" 
                    onClick={() => setShowCompletionModal(false)}
                    style={{
                        borderRadius: "50px",
                        paddingLeft: "30px",
                        paddingRight: "30px"
                    }}
                >
                    {t("dashboard.ui.gotIt")}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    // Custom Alert Modal - Replaces native alert() with translatable modal
    const showAlert = (message) => {
        setCustomAlertMessage(message);
        setShowCustomAlert(true);
    };

    const CustomAlertModal = () => (
        <Modal show={showCustomAlert} onHide={() => setShowCustomAlert(false)} centered>
            <Modal.Header 
                closeButton
                style={{ 
                    backgroundColor: dark ? "#1e1e1e" : "#f8f9fa",
                    borderBottom: `1px solid ${dark ? "#444" : "#dee2e6"}`
                }}
            >
                <Modal.Title style={{ 
                    color: dark ? "#fff" : "#333",
                    fontSize: "16px",
                    fontWeight: "400",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                }}>
                    <span style={{ fontWeight: "600" }}>Sallie_Autism</span>
                    <span>{t("dashboard.customAlert.says")}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ 
                backgroundColor: dark ? "#2d2d2d" : "#fff",
                color: dark ? "#ddd" : "#333",
                padding: "20px"
            }}>
                <p style={{ marginBottom: "0", fontSize: "14px" }}>
                    {customAlertMessage}
                </p>
            </Modal.Body>
            <Modal.Footer style={{ 
                backgroundColor: dark ? "#2d2d2d" : "#fff",
                borderTop: `1px solid ${dark ? "#444" : "#dee2e6"}`,
                justifyContent: "flex-end"
            }}>
                <Button 
                    variant="primary" 
                    onClick={() => setShowCustomAlert(false)}
                    style={{
                        backgroundColor: "#007bff",
                        border: "none",
                        padding: "6px 30px",
                        fontSize: "13px",
                        fontWeight: "400"
                    }}
                >
                    {t("dashboard.customAlert.okButton")}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    // Level-Up Modal — appears right after the 5th consecutive "+" completes a level's
    // progress bar to 5/5. "Save Progress" fires the real level-up call and advances
    // the exercise to the next level at 0/5. backdrop="static"/keyboard={false} so the
    // patient can't dismiss it without confirming — that confirmation IS the level-up.
    const LevelUpModal = () => {
        if (!levelUpExercise) return null;

        const fromLevel = parseInt(levelUpExercise.exercise_level, 10);
        const toLevel = fromLevel + 1;
        const exerciseName =
            tExerciseName(levelUpExercise.exercise_name) ?? levelUpExercise.exercise_name;

        return (
            <Modal show={showLevelUpModal} centered backdrop="static" keyboard={false}>
                <Modal.Header
                    style={{
                        backgroundColor: dark ? "#1e1e1e" : "#f8f9fa",
                        borderBottom: `1px solid ${dark ? "#444" : "#dee2e6"}`
                    }}
                >
                    <Modal.Title style={{ color: dark ? "#fff" : "#333", fontSize: "16px", fontWeight: "600" }}>
                        {t("dashboard.levelUpModal.title")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{
                    backgroundColor: dark ? "#2d2d2d" : "#fff",
                    color: dark ? "#ddd" : "#333",
                    padding: "20px"
                }}>
                    <p style={{ marginBottom: levelUpSaveError ? "10px" : "0", fontSize: "14px" }}>
                        {t("dashboard.levelUpModal.message")
                            .replace('{level}', fromLevel)
                            .replace('{exercise}', exerciseName)
                            .replace('{next}', toLevel)}
                    </p>
                    {levelUpSaveError && (
                        <p style={{ color: '#dc3545', fontSize: '13px', fontWeight: '600', marginBottom: '0' }}>
                            {t("dashboard.levelUpModal.saveError")}
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer style={{
                    backgroundColor: dark ? "#2d2d2d" : "#fff",
                    borderTop: `1px solid ${dark ? "#444" : "#dee2e6"}`,
                    justifyContent: "flex-end"
                }}>
                    <Button
                        variant="primary"
                        onClick={handleSaveLevelUpProgress}
                        disabled={savingLevelUp}
                        style={{
                            backgroundColor: "#28a745",
                            border: "none",
                            padding: "6px 30px",
                            fontSize: "13px",
                            fontWeight: "500"
                        }}
                    >
                        {savingLevelUp ? t("dashboard.levelUpModal.savingButton") : t("dashboard.levelUpModal.saveButton")}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    // Decrement Confirm Modal — "Are you sure?" guard shown when the patient clicks "-".
    // Progress isn't touched until "Save" is clicked; closing the modal (X) cancels
    // with no change, same as clicking "-" never happened.
    const DecrementConfirmModal = () => {
        if (!decrementExercise) return null;

        return (
            <Modal
                show={showDecrementConfirmModal}
                onHide={() => { setShowDecrementConfirmModal(false); setDecrementExercise(null); }}
                centered
            >
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: dark ? "#1e1e1e" : "#f8f9fa",
                        borderBottom: `1px solid ${dark ? "#444" : "#dee2e6"}`
                    }}
                >
                    <Modal.Title style={{ color: dark ? "#fff" : "#333", fontSize: "16px", fontWeight: "600" }}>
                        {t("dashboard.decrementConfirmModal.title")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{
                    backgroundColor: dark ? "#2d2d2d" : "#fff",
                    color: dark ? "#ddd" : "#333",
                    padding: "20px"
                }}>
                    <p style={{ marginBottom: "0", fontSize: "14px" }}>
                        {t("dashboard.decrementConfirmModal.message")}
                    </p>
                </Modal.Body>
                <Modal.Footer style={{
                    backgroundColor: dark ? "#2d2d2d" : "#fff",
                    borderTop: `1px solid ${dark ? "#444" : "#dee2e6"}`,
                    justifyContent: "flex-end"
                }}>
                    <Button
                        onClick={handleConfirmDecrement}
                        style={{
                            backgroundColor: "#6f42c1",
                            border: "none",
                            padding: "6px 30px",
                            fontSize: "13px",
                            fontWeight: "500"
                        }}
                    >
                        {t("dashboard.decrementConfirmModal.saveButton")}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    if (width >= 768) {
        return (
            <>
                {VerifyEmailMessage}
                {RateLimitModal()}
                {CompletionModal()}
                {CustomAlertModal()}
                {LevelUpModal()}
                {DecrementConfirmModal()}
                <Row>
                    <Col md={9}>
                        {user.userType === "provider" && (
                            <Sallie
                                user={user}
                                text={getMessage("sallieText")}
                                isProvider={true}
                            />
                        )}
                        {user.userType === "caregiver" && (
                            <Sallie
                                user={user}
                                text={getMessage("sallieText")}
                                isCG={true}
                            />
                        )}
                        {user.userType === "patient" && (
                            <Sallie user={user} text={getMessage("sallieText")} />
                            
                        )}
                    </Col>
                </Row>

                {/* End top row */}
                <Row className="d-flex align-items-start">
                    <ThemeChangerButton />
                    {ConditionalCurrentPatient()}
                    {ConditionalSendMessage()}
                    <VideoTutorialsButton />
                </Row>

                <Row>
                    <Col md={4}>
                        {/* <ConditionalTherapyTracker /> */}
                        <AssignedExercises />
                        <ProviderExerciseAssignment />
                        {user.userType !== "provider" && <EditNotification />}
                        {user.userType === "provider" && patient && (
                            <>
                                <h6 className="fw-bold mb-2">{t("dashboard.clinicInfo.title")}</h6>
                                <div className="card p-3 mb-3">
                                    <p className="mb-1"><strong>{t("dashboard.clinicInfo.clinicName")}</strong> {patient.clinic_name || "—"}</p>
                                    <p className="mb-0"><strong>{t("dashboard.clinicInfo.clinicLocation")}</strong> {patient.clinic_location || "—"}</p>
                                </div>
                            </>
                        )}
                        {ConditionalAssignmentButton()}
                        {ConditionalOnOffButtonMessages()}
                        {ConditionalOnOffButtonAlert()}
                        {ConditionalPatientStatus()}
                        {ConditionalHemisphericSum()}
                        <ConditionalCalendar />
                    </Col>
                    <Col md={8}>
                        {CurrentlyAssignedExercises()}
                        {ConditionalSelectedExercises()}
                        {ConditionalFeelingToday}
                        {ConditionalSymptomTracker}
                        {ConditionalAddSymptoms}
                        {ConditionalCombinedChart}
                    </Col>
                </Row>
            </>
        );
    } else {
        return (
            <>
                {VerifyEmailMessage}
                {RateLimitModal()}
                {CompletionModal()}
                {CustomAlertModal()}
                {LevelUpModal()}
                {DecrementConfirmModal()}
                <Row>
                    <Col md={8}>
                        {userType === "provider" && (
                            <Sallie
                                text={getMessage("sallieText")}
                                isProvider={true}
                            />
                        )}
                        {userType === "caregiver" && (
                            <Sallie
                                text={getMessage("sallieText")}
                                isCG={true}
                            />
                        )}
                        {userType === "patient" && (
                            <Sallie text={getMessage("sallieText")} />
                        )}
                    </Col>
                </Row>

                {/* End top row */}
                <Row className="d-flex align-items-center">
                    <ThemeChangerButton />
                    {ConditionalCurrentPatient()}
                    {ConditionalSendMessage()}
                    <VideoTutorialsButton />
                </Row>
                <Row className="d-flex align-items-center justify-content-center">
                    <Col md={4}>
                        {/* <ConditionalTherapyTracker /> */}
                        <AssignedExercises />
                        <ProviderExerciseAssignment />
                        {/* <VideoExerciseTracker /> -> This is the old green 'Track Exercises' component */}
                        {user.userType !== "provider" && <EditNotification />}
                        {user.userType === "provider" && patient && (
                            <>
                                <h6 className="fw-bold mb-2">{t("dashboard.clinicInfo.title")}</h6>
                                <div className="card p-3 mb-3">
                                    <p className="mb-1"><strong>{t("dashboard.clinicInfo.clinicName")}</strong> {patient.clinic_name || "—"}</p>
                                    <p className="mb-0"><strong>{t("dashboard.clinicInfo.clinicLocation")}</strong> {patient.clinic_location || "—"}</p>
                                </div>
                            </>
                        )}
                        {ConditionalAssignmentButton()}
                        {ConditionalOnOffButtonMessages()}
                        {ConditionalOnOffButtonAlert()}
                        {ConditionalPatientStatus()}
                        {ConditionalHemisphericSum()}
                        <ConditionalCalendar />
                    </Col>

                    <Col md={8}>
                        {CurrentlyAssignedExercises()}
                        {ConditionalSelectedExercises()}
                        {ConditionalFeelingToday}
                        {ConditionalSymptomTracker}
                        {ConditionalAddSymptoms}
                        {ConditionalCombinedChart}
                    </Col>
                </Row>
            </>
        );
    }
};

export default Dashboard;